import { OrderAiCostLine } from '@/components/admin/OrderAiCostLine';
import { AlbumThemeBar } from '@/components/theme/ThemeBar';
import QuickBuildProgress from '@/components/QuickBuildProgress';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { StepDesign } from '@/pages/customer/AlbumFlowPage';
import { pickerThumbUrl } from '@/lib/coverImageSource';
import {
  ordersApi, settingsApi, uploadLinkApi, photosApi, sessionsApi, coversApi, redosApi, printApi,
  previewLinkApi, stockApi, resumeLinkApi,
  type Order, type OrderRedo, type RedoScope, type RedoReason, type PreviewNote, type Cover, type CoverCategory,
} from '@/lib/api';
import { OrderProfitBody } from './AdminStockPage';
import { renderAndUploadRedoPdfs, buildAlbumPdfInput } from '@/lib/multiAlbumPdf';
import { generateCoverPdf } from '@/lib/pdfGenerator';
import { isUploadWindowOpen } from '@/lib/editorAccess';
import {
  prepareInteriorPdf, estimatePeakMemoryMb, InteriorPrepareError,
  type PrepareResult,
} from '@/lib/interiorPrepare';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { AdminLayout } from '@/components/AdminLayout';
import { OrderUpsellsCard } from '@/components/admin/OrderUpsellsCard';
import { OrderCouponCard } from '@/components/admin/OrderCouponCard';
import { LivingPhotoCard } from '@/components/admin/LivingPhotoCard';
import { OrderAddonsCard } from '@/components/admin/OrderAddonsCard';
import { OrderExtrasLine } from '@/components/admin/OrderExtrasLine';
import { resolveAlbumIndexes } from '@/lib/albumIndexes';
import { AmeexDeliveryCard } from '@/components/admin/AmeexDeliveryCard';
import { ReliefPriceField } from '@/components/admin/ReliefPriceField';
import { AgentJourneyCard } from '@/components/admin/AgentJourneyCard';
import { OrderConversationCard } from '@/components/admin/OrderConversationCard';
import { PaymentRequestButton } from '@/components/admin/PaymentRequestModal';
import { AdminAlbumPhotosModal } from '@/components/admin/AdminAlbumPhotosModal';
import { PhotoDedupLine } from '@/components/admin/PhotoDedupLine';
import { AlbumPdfImportControl } from './order/AlbumPdfImportControl';
import { SourceBadge, sourceLabel } from '@/components/SourceBadge';
import { OriginSelect } from '@/components/admin/OriginSelect';
import { DeviceBadge, deviceLabel } from '@/components/DeviceBadge';
import { formatDate, formatPrice, formatPhone, ORDER_STATUS_LABELS } from '@/lib/utils';
import { orderActionState } from '@/lib/orderActionState';
import { computeOrderPrice, resolvePricePerPage } from '@/lib/orderPricing';
import { deriveSubState, albumCountForBundle } from '@/lib/followUpStateMachine';
import { SUB_STATES_WITH_LINK, SUB_STATE_LABELS, renderMessage, followUpMessage, ladderLength, computeAcompte, PREVIEW_PITCH_SUB_STATES, renderPreviewPitch } from '@/lib/followUpMessages';
import { generateAssemblyTemplatePdf, DEFAULT_PRINT_SETTINGS, computeVinylSizeMm, withDerivedVinyl, type PrintSettings } from '@/lib/assemblyTemplate';
import { uploadToSignedUrl } from '@/lib/uploadToSignedUrl';
import { paddedInteriorPageCount } from '@/lib/interiorPagePadding';
import { printsIntro } from '@/lib/bookStructure';
import { previewPlanText, planIsHeld, type PreviewPlanLike } from '@/lib/sendVisibility';
import { useT } from '@/lib/i18n';
import { usePermissions } from '@/lib/permissionsStore';
import { useAuthStore } from '@/store/useAuthStore';
import { askOverrideReason, isNotPaid } from '@/lib/forceWithoutAdvance';
import { changeStatusAsking } from '@/lib/statusChangeAsk';
import { OrderAssignmentControl } from '@/components/admin/OrderAssignmentControl';
import { OrderBarShortcuts } from '@/components/admin/OrderBarShortcuts';
import { ClientRequestsCard } from '@/components/admin/ClientRequestsCard';
import { clientRequestsApi } from '@/lib/clientRequestsApi';
import { pilotageApi } from '@/lib/api';
import { useIsPhone } from '@/lib/phoneViewport';
import { useChatOpenSignal } from './assistant/phone';
import {
  ActivityCard, ChatDrawer, MoneyBlock, PinnedProblems, StepBar, StepFold, STEP_LABEL, stepOrder, useOrderNotes,
  type StepInfo, type StepKey,
} from './order/OrderShell';
import { albumName } from '@/lib/titleSource';
import { CreatedBecause } from '@/components/admin/CreatedBecause';

// ─── PDF Status Badge ────────────────────────────────────────────────────────

function PdfStatusBadge({ status }: { status: Order['pdf_status'] }) {
  const { t } = useT();
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
        {t('Non généré')}
      </span>
    );
  }
  const config = {
    generating: { dot: 'bg-amber-400 animate-pulse', bg: 'bg-amber-50', text: 'text-amber-700', label: t('Génération en cours...') },
    ready:      { dot: 'bg-green-500',                bg: 'bg-green-50',  text: 'text-green-700',  label: t('PDFs prêts') },
    failed:     { dot: 'bg-red-500',                  bg: 'bg-red-50',    text: 'text-red-700',    label: t('Erreur') },
  }[status];

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {status === 'generating'
        ? <span className="w-3 h-3 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
        : <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      }
      {config.label}
    </span>
  );
}

// ─── Copy Link Button ────────────────────────────────────────────────────────

function CopyLinkButton({ url }: { url: string }) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={copy}
      title={t('Copier le lien')}
      className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border ${copied
        ? 'bg-[#f0fdf4] text-[#16a34a] border-[#86efac] dark:bg-status-done-bg dark:text-status-done dark:border-status-done-border'
        : 'bg-adm-surface-2 text-adm-muted border-adm-line'}`}
    >
      {copied ? t('✓ Copié') : t('📋 Copier')}
    </button>
  );
}

// ─── Cover-picker link for the client (2026-08-12) ───────────────────────────
// One link for the WHOLE order, not per album: on a Duo/Trio the page walks the
// client through album 1, then 2, then 3, and finishes on a done screen. Sent
// over WhatsApp so the client picks their own cover from the 250-design
// catalogue instead of an agent guessing and redoing it later.
//
// `?pick=1` is what separates this from the internal editor route: without it
// the page bounces to the editor as soon as a cover exists, which would make
// the link useless on exactly the orders where the team already set one.
function CoverPickLinkRow({ shopifyOrderId }: { shopifyOrderId: string }) {
  const { t } = useT();
  const url = `${window.location.origin}/editor/${shopifyOrderId}/1/covers?pick=1`;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-gray-500 shrink-0">{t('Lien couverture client')}</span>
      <input
        readOnly
        value={url}
        onFocus={(e) => e.currentTarget.select()}
        className="flex-1 min-w-[200px] px-2.5 py-1.5 rounded-lg border border-gray-200 text-[11px] font-mono text-gray-700 bg-gray-50"
      />
      <CopyLinkButton url={url} />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 text-[11px] text-brand-600 hover:underline"
      >
        {t('Ouvrir')}
      </a>
    </div>
  );
}

// ─── PDF File Row ────────────────────────────────────────────────────────────

// Redesign 2026-06-10 (Yahya): the whole row IS the button — opens the stored
// (Supabase) PDF in a new tab. Big always-visible affordance instead of the
// old hover-revealed "Ouvrir"; copy stays as a secondary action that doesn't
// trigger the navigation.
function PdfFileRow({ icon, label, url }: { icon: string; label: string; url: string }) {
  const { t } = useT();
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all group cursor-pointer hover:-translate-y-px"
      style={{
        background: 'rgba(201, 162, 74, 0.07)',
        borderColor: 'rgba(201, 162, 74, 0.30)',
      }}
    >
      <span className="text-2xl">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-adm-gold-ink">{label}</p>
        <p className="text-[11px] text-gray-400 truncate mt-0.5">{url.slice(0, 70)}…</p>
      </div>
      <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        <CopyLinkButton url={url} />
      </span>
      <span
        className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold text-white transition-colors"
        style={{ background: 'linear-gradient(135deg, #C9A24A, #A8842F)', boxShadow: '0 2px 8px rgba(201,162,74,.3)' }}
      >
        {t('Ouvrir')}
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
          <path d="M7 17L17 7M9 7h8v8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </a>
  );
}

// ─── Spine size helper ─────────────────────────────────────────────────────
// Paper: papier couché 170 g/m². Calibre typique 0.155 mm par feuille (1 feuille = 2 pages).
// Si on change de grammage ou de finition, mettre à jour cette constante seule —
// le calcul du dos en aval reste correct sans autre modification.
const PAPER_THICKNESS_MM = 0.155;
const PAPER_LABEL = 'couché 170 g/m²';

function spineSize(pageCount: number): { mm: number; cm: number; sheets: number } {
  const sheets = Math.ceil(pageCount / 2);
  const mm = sheets * PAPER_THICKNESS_MM;
  return {
    sheets,
    mm: Math.round(mm * 100) / 100,
    cm: Math.round((mm / 10) * 100) / 100,
  };
}

// Inline pill that summarizes the spine width for one album.
// `themed`: the album opens on its themed S1 page (album_pdfs[].themed_book) —
// such a book prints NO intro page (design system v2 §7 S1, bookStructure.ts).
function SpineInfo({ pageCount, themed = false }: { pageCount: number; themed?: boolean }) {
  const { t } = useT();
  if (!pageCount || pageCount <= 0) return null;
  const s = spineSize(pageCount);
  // Printed total = content pages + intro/outro bookends, rounded up to a
  // multiple of 4 (the bindery images each album's interior on A3 sheets,
  // double-sided → 4 album pages per sheet). PRINT-ONLY — does NOT change the
  // priced page count (`pageCount` above) shown elsewhere; blank filler pages
  // are never charged. Assumption: this component doesn't have the per-order
  // intro/outro URLs, so we assume the brand has BOTH bookends (the standard
  // Storiz config) — and the book says whether its intro prints: a themed
  // book's does not (printsIntro). If a bookend is missing, the real printed
  // total is still a multiple of 4 — only this estimate could differ by 4.
  const printedTotal = paddedInteriorPageCount(pageCount, printsIntro(themed, true), true);
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-50/60 to-transparent dark:from-[rgb(245_158_11/0.14)] rounded-lg border border-amber-100">
      <span className="text-base">📏</span>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-gray-800">
          {t('Dos')} · <span className="text-amber-800">{s.mm.toFixed(2)} mm</span>
          <span className="text-gray-400 font-normal"> · {s.cm.toFixed(2)} cm</span>
        </div>
        <div className="text-[11px] text-gray-500">
          {t('{n} pages', { n: pageCount })} · {s.sheets} {s.sheets > 1 ? t('feuilles') : t('feuille')} · {t('papier {label}', { label: PAPER_LABEL })}
        </div>
        <div className="text-[11px] text-gray-500">
          {t('Pages imprimées : {n} (multiple de 4 pour impression A3)', { n: printedTotal })}
        </div>
      </div>
    </div>
  );
}

// ─── Fiche de montage (assembly template) download button ─────────────────────
// Downloads the bindery's board-placement sheet for ONE album, generated
// client-side from the global print settings. This is the UNIVERSAL fiche —
// the same sheet for every order — and its fold/cut/spine marks match the guide
// lines printed on the cover, so the cover lays straight onto it. SEPARATE sheet,
// never printed on the cover artwork.
function FicheMontageButton({ settings }: { settings: PrintSettings }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  async function download() {
    setBusy(true);
    try {
      const bytes = await generateAssemblyTemplatePdf(settings);
      const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fiche-montage-storiz.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      onClick={download}
      disabled={busy}
      title={t('Télécharger la fiche de montage universelle (positionnement des cartons rigides). Une seule, réutilisable pour tous les albums — ses traits correspondent aux traits imprimés sur la couverture.')}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-brand-300 transition-colors disabled:opacity-50"
    >
      <span aria-hidden>📐</span>
      {busy ? t('Génération…') : t('Fiche de montage')}
    </button>
  );
}

// Admin-only NON-DESTRUCTIVE cover preview. Builds the cover PDF from the
// album's CURRENT session (same loader the submit path uses → buildAlbumPdfInput)
// and opens it — but never uploads, never finalizes, never changes order status
// or creates a colis. Assembly guides are ON so the cover's fold/cut/spine marks
// can be checked against the fiche de montage. Works before OR after submission
// as long as the album has a composed session.
function CoverPreviewButton({
  shopifyOrderId,
  albumIndex,
  albumCount,
  settings,
  coverOverridden = false,
}: {
  shopifyOrderId: string;
  albumIndex: number;
  albumCount: number;
  settings: { logo_url?: string | null; intro_page_1_url?: string | null; outro_page_2_url?: string | null; pdf_bleed_mm?: number } | undefined;
  /** True when a manual cover is in force for this album — this preview would
   *  then show the generated cover, which is NOT what gets printed. */
  coverOverridden?: boolean;
}) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const { data: coversData } = useQuery({ queryKey: ['covers-all'], queryFn: () => coversApi.list() });
  async function preview() {
    setBusy(true);
    // Open the tab synchronously (inside the click) so the browser's popup
    // blocker allows it; we point it at the blob once the PDF is ready.
    const win = window.open('', '_blank');
    try {
      const input = await buildAlbumPdfInput({
        shopifyOrderId,
        albumIndex,
        covers: coversData?.covers ?? [],
        logoUrl: settings?.logo_url ?? null,
        introPageUrl: settings?.intro_page_1_url ?? null,
        outroPageUrl: settings?.outro_page_2_url ?? null,
        bleedMm: settings?.pdf_bleed_mm ?? 3,
        showAssemblyGuides: true, // verify the cover guides vs the fiche
      });
      if (!input) {
        win?.close();
        alert(t("Aucune mise en page pour l'album {n} — le client n'a pas encore composé cet album.", { n: albumIndex }));
        return;
      }
      const bytes = await generateCoverPdf(input); // cover only — no interior, no upload
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
      if (win) win.location.href = url;
      else window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
    } catch (e) {
      win?.close();
      console.error('[cover-preview] failed:', e);
      alert(t("Échec de la génération de l'aperçu couverture. Voir la console."));
    } finally {
      setBusy(false);
    }
  }
  const base = albumCount > 1 ? t('Aperçu couv. A{n}', { n: albumIndex }) : t('Aperçu couverture');
  const label = coverOverridden ? t('{base} (générée)', { base }) : base;
  return (
    <button
      onClick={preview}
      disabled={busy}
      title={coverOverridden
        ? t("⚠ Cet album utilise une couverture remplacée manuellement. Ce bouton régénère la couverture depuis la mise en page du client — ce n'est PAS le fichier qui sera imprimé. Pour voir la vraie couverture, ouvrez le fichier « Couverture A3+ » dans la liste ci-dessous.")
        : t('Génère et ouvre la couverture A3+ (avec repères de montage) SANS soumettre la commande ni changer son statut — aperçu uniquement.')}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 ${
        coverOverridden
          ? 'border-violet-200 text-violet-700 bg-violet-50/50 hover:bg-violet-50'
          : 'border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-brand-300'
      }`}
    >
      <span aria-hidden>{coverOverridden ? '⚠️' : '🖼️'}</span>
      {busy ? t('Génération…') : label}
    </button>
  );
}

// ─── Manual cover replacement (migration 067) ────────────────────────────────
// Replaces the COVER ASSET ONLY — the interior PDF and the page layout are
// untouched and keep regenerating normally.
//
// The replacement is written to the same storage path the generated cover uses,
// so every already-stored signed URL (this page, Shopify note_attributes, the
// printer's download link) is path-bound to the new bytes and serves them at
// once. The customer 3D preview is the one consumer that does NOT track the
// PDF — it renders from JPEGs rasterized from the cover once — so the worker
// explicitly re-triggers a preview render after a successful replacement.
//
// Dimensions are validated against the LIVE app_settings print template, never
// a hardcoded 482 × 346, so changing the print settings never starts rejecting
// files the generator itself now produces.
function CoverReplaceDialog({
  shopifyOrderId,
  albumIndex,
  expected,
  kind = 'cover',
  onClose,
  onDone,
}: {
  shopifyOrderId: string;
  albumIndex: number;
  /** Print target. The interior flow also needs the live bleed setting and the
   *  branding page URLs, since it now prepares the file in the browser. */
  expected: {
    wmm: number; hmm: number;
    bleedMm?: number;
    introPageUrl?: string | null;
    outroPageUrl?: string | null;
    orderLabel?: string | null;
  };
  /** Which print asset this dialog replaces. Cover and interior share the
   *  whole flow — spec panel, drop zone, reject-with-numbers — and differ only
   *  in the endpoint, the accepted formats and the wording. */
  kind?: 'cover' | 'interior';
  onClose: () => void;
  onDone: () => void;
}) {
  const isCover = kind === 'cover';
  const { t } = useT();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [step, setStep] = useState<string>('');
  const [pct, setPct] = useState(0);
  const [prepared, setPrepared] = useState<PrepareResult | null>(null);
  const [error, setError] = useState<{ message: string; expected?: { wmm: number; hmm: number }; actual?: { wmm: number; hmm: number } } | null>(null);

  // Every stage the interior pipeline can report, with the share of the bar it
  // owns. A determinate bar matters here: preparing a 300 MB book is a
  // multi-minute job and a bare spinner gives the operator no idea whether it
  // is working or wedged.
  const INTERIOR_STAGES: Record<string, { label: string; pct: number }> = {
    reading:    { label: t('Lecture du fichier…'),        pct: 10 },
    measuring:  { label: t('Mesure de chaque page…'),      pct: 30 },
    converting: { label: t('Conversion A4 → 200 × 297…'),  pct: 45 },
    branding:   { label: t('Ajout des pages Storiz…'),     pct: 55 },
    padding:    { label: t('Calage sur un multiple de 4…'), pct: 65 },
    checking:   { label: t('Vérification pour l’impression…'), pct: 70 },
    saving:     { label: t('Préparation du fichier…'),     pct: 75 },
  };

  /** Phase 1 (interior only) — do the work in the browser, then show a summary
   *  so the operator confirms a crop BEFORE anything reaches the print path. */
  async function analyse() {
    if (!file) return;
    setBusy(true); setError(null); setPct(0);
    try {
      const needMb = estimatePeakMemoryMb(file.size);
      // A phone will simply die on a large book. Say so instead of crashing.
      const deviceMemGb = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
      if (deviceMemGb && needMb > deviceMemGb * 1024 * 0.55) {
        throw new InteriorPrepareError(
          t('Fichier trop lourd pour cet appareil ({mb} Mo — il faut environ {need} Mo de mémoire). Ouvrez cette page sur un ordinateur.', {
            mb: Math.round(file.size / 1048576), need: needMb,
          }),
        );
      }

      const bytes = new Uint8Array(await file.arrayBuffer());
      const res = await prepareInteriorPdf(bytes, {
        bleedMm: expected.bleedMm ?? 3,
        introPageUrl: expected.introPageUrl ?? null,
        outroPageUrl: expected.outroPageUrl ?? null,
        orderLabel: expected.orderLabel ?? null,
        filename: file.name,
        onProgress: (s) => {
          const stage = INTERIOR_STAGES[s];
          if (stage) { setStep(stage.label); setPct(stage.pct); }
        },
      });
      setPrepared(res);
      setStep(''); setPct(0);
    } catch (e) {
      setError({ message: e instanceof InteriorPrepareError ? e.message : String((e as Error)?.message ?? e) });
      setStep(''); setPct(0);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      if (isCover) {
        setStep(t('Téléversement…')); setPct(20);
        const signed = await ordersApi.coverReplaceUploadUrl(shopifyOrderId, albumIndex, file.name);
        await uploadToSignedUrl(signed.upload_url, file, signed.content_type, (p) => setPct(20 + p * 0.6));
        setStep(t('Vérification des dimensions…')); setPct(85);
        const res = await ordersApi.coverReplaceFinalize(shopifyOrderId, albumIndex, signed.storage_path, file.name);
        setStep(''); setPct(0);
        onDone();
        alert(t('Couverture remplacée ({w} × {h} mm). Les aperçus 3D du client sont en cours de régénération.', {
          w: res.dimensions.wmm, h: res.dimensions.hmm,
        }));
        onClose();
        return;
      }

      // INTERIOR — the file was measured, converted and padded in the browser
      // (the worker cannot open a 300 MB book: it gets 128 MB of memory and
      // pdf-lib peaks near 3.3× the file size). Upload the PREPARED bytes and
      // hand the worker the report as the audit trail.
      const ready = prepared;
      if (!ready) return;
      const outFile = ready.unchanged
        ? file
        : new File([ready.bytes as BlobPart], file.name, { type: 'application/pdf' });

      setStep(t('Téléversement…')); setPct(80);
      const signed = await ordersApi.interiorReplaceUploadUrl(shopifyOrderId, albumIndex, file.name);
      await uploadToSignedUrl(signed.upload_url, outFile, signed.content_type, (p) => setPct(80 + p * 0.15));

      setStep(t('Finalisation…')); setPct(97);
      const res = await ordersApi.interiorReplaceFinalize(
        shopifyOrderId, albumIndex, signed.storage_path, file.name,
        ready.log as unknown as Record<string, unknown>,
      );
      setStep(''); setPct(0);
      onDone();
      alert(t('Intérieur remplacé ({n} pages). Les aperçus 3D du client sont en cours de régénération.', { n: res.pages }));
      onClose();
    } catch (e) {
      const body = (e as { body?: Record<string, unknown> })?.body ?? {};
      setError({
        message: String(body.message ?? (e as Error)?.message ?? t('Échec du remplacement.')),
        expected: body.expected as { wmm: number; hmm: number } | undefined,
        actual: body.actual as { wmm: number; hmm: number } | undefined,
      });
      setStep(''); setPct(0);
    } finally {
      setBusy(false);
    }
  }

  // The two things that actually go wrong here are picking the wrong file and
  // not knowing what "the right file" is. So the target size is stated once,
  // large, at the top; the drop area repeats it; and a rejection shows the two
  // numbers side by side instead of burying them in a sentence.
  const isSvg = !!file && /\.svg$/i.test(file.name);
  const sizeKb = file ? Math.round(file.size / 1024) : 0;

  return (
    <Modal
      isOpen
      onClose={busy ? () => {} : onClose}
      title={isCover
        ? t('Remplacer la couverture — album {n}', { n: albumIndex })
        : t('Remplacer l’intérieur — album {n}', { n: albumIndex })}
      size="md"
    >
      <div className="space-y-4">
        {/* Target spec — the number the operator is trying to match. */}
        <div className="rounded-xl border border-brand-200 bg-brand-50/60 px-4 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-700">
              {isCover ? t('Format exigé') : t('Format exigé — chaque page')}
            </span>
            <span className="text-[11px] text-brand-700/70">{t('tolérance ±2 mm')}</span>
          </div>
          <p className="mt-0.5 text-2xl font-bold tabular-nums text-gray-900">
            {expected.wmm} <span className="text-gray-400">×</span> {expected.hmm} <span className="text-base font-semibold text-gray-500">mm</span>
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-gray-600">
            {isCover
              ? t("Calculé depuis les réglages d'impression actuels (carton + tranche + rabats + zone de sécurité).")
              : t('Page finie. Un export avec fond perdu ({bw} × {bh} mm) est également accepté. Toutes les pages doivent avoir la même taille.', {
                  bw: expected.wmm + 6, bh: expected.hmm + 6,
                })}
          </p>
        </div>

        {/* Drop area — a click target and a drop target, with the accepted
            formats stated where the file is chosen rather than in a footnote. */}
        <div>
          <label
            onDragOver={(e) => { e.preventDefault(); if (!busy) setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault(); setDragging(false);
              if (busy) return;
              const f = e.dataTransfer.files?.[0];
              if (f) { setFile(f); setError(null); setPrepared(null); }
            }}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
              busy ? 'cursor-not-allowed opacity-60 border-gray-200' :
              dragging ? 'border-brand-400 bg-brand-50' :
              file ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-300 hover:border-brand-300 hover:bg-gray-50'
            }`}
          >
            <input
              type="file"
              accept={isCover ? '.pdf,.svg,application/pdf,image/svg+xml' : '.pdf,application/pdf'}
              disabled={busy}
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setError(null); setPrepared(null); }}
              className="sr-only"
            />
            {file ? (
              <>
                <span className="text-2xl" aria-hidden>{isSvg ? '🖊️' : '📄'}</span>
                <span className="mt-1 max-w-full truncate text-sm font-semibold text-gray-900">{file.name}</span>
                <span className="mt-0.5 text-[11px] text-gray-500">
                  {isSvg ? t('SVG') : t('PDF')} · {sizeKb} Ko · {t('cliquez pour changer')}
                </span>
              </>
            ) : (
              <>
                <span className="text-2xl" aria-hidden>📤</span>
                <span className="mt-1 text-sm font-medium text-gray-700">
                  {t('Glissez le fichier ici ou cliquez pour choisir')}
                </span>
                <span className="mt-0.5 text-[11px] text-gray-500">
                  {isCover
                    ? t('PDF d\'une seule page, ou SVG en millimètres')
                    : t('PDF multi-pages, toutes au même format')}
                </span>
              </>
            )}
          </label>
          <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
            {isCover
              ? t('Le SVG doit déclarer ses dimensions en unités réelles — width="{w}mm" height="{h}mm". Un SVG en pixels ou avec un simple viewBox n\'a pas de taille physique et sera refusé, tout comme les images JPG/PNG.', { w: expected.wmm, h: expected.hmm })
              : t('Chaque page est mesurée dans votre navigateur. Le A4 standard (210 × 297 mm) est accepté et converti automatiquement en rognant 5 mm de chaque côté. Les pages intro/outro Storiz sont ajoutées et le total est calé sur un multiple de 4. Toutes les pages doivent avoir la même taille.')}
          </p>
        </div>

        {/* Determinate progress. Preparing a 300 MB book takes minutes, and a
            bare spinner leaves the operator unable to tell working from stuck. */}
        {busy && step && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <span aria-live="polite" className="text-xs font-medium text-gray-700">{step}</span>
              <span className="text-xs font-bold tabular-nums text-gray-500">{Math.round(pct)}%</span>
            </div>
            <div
              className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
              role="progressbar"
              aria-valuenow={Math.round(pct)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={step}
            >
              <div
                className="h-full rounded-full bg-brand-500 transition-[width] duration-300 ease-out"
                style={{ width: `${Math.max(3, Math.min(100, pct))}%` }}
              />
            </div>
          </div>
        )}

        {/* What we are about to do — shown BEFORE anything reaches the print
            path, because the A4 crop removes 5 mm of the client's artwork and
            that has to be a decision, not a surprise. */}
        {!isCover && prepared && !busy && (
          <div className={`rounded-xl border px-4 py-3 ${
            prepared.log.method === 'crop-a4' ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'
          }`}>
            <p className={`text-sm font-semibold ${prepared.log.method === 'crop-a4' ? 'text-amber-900' : 'text-emerald-900'}`}>
              {prepared.log.method === 'crop-a4'
                ? t('A4 détecté — 5 mm seront rognés de chaque côté')
                : t('Format correct — aucune conversion nécessaire')}
            </p>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
              <dt className="text-gray-600">{t('Format reçu')}</dt>
              <dd className="font-semibold tabular-nums text-gray-900">
                {prepared.log.source_mm.wmm} × {prepared.log.source_mm.hmm} mm
              </dd>
              <dt className="text-gray-600">{t('Format imprimé')}</dt>
              <dd className="font-semibold tabular-nums text-gray-900">
                {prepared.log.target_mm.wmm} × {prepared.log.target_mm.hmm} mm
              </dd>
              <dt className="text-gray-600">{t('Pages du fichier')}</dt>
              <dd className="font-semibold tabular-nums text-gray-900">{prepared.log.source_pages}</dd>
              {prepared.log.branding_pages_added > 0 && (
                <>
                  <dt className="text-gray-600">{t('Pages Storiz ajoutées')}</dt>
                  <dd className="font-semibold tabular-nums text-gray-900">+{prepared.log.branding_pages_added}</dd>
                </>
              )}
              {prepared.log.blank_pages_added > 0 && (
                <>
                  <dt className="text-gray-600">{t('Pages blanches (calage ÷4)')}</dt>
                  <dd className="font-semibold tabular-nums text-gray-900">+{prepared.log.blank_pages_added}</dd>
                </>
              )}
              <dt className="text-gray-600 font-medium">{t('Total imprimé')}</dt>
              <dd className="font-bold tabular-nums text-gray-900">{prepared.log.final_pages} {t('pages')}</dd>
            </dl>
            {prepared.log.method === 'crop-a4' && (
              <p className="mt-2 text-[11px] leading-relaxed text-amber-800">
                {t('Tout élément situé à moins de 5 mm du bord gauche ou droit sera coupé. La hauteur ne change pas.')}
              </p>
            )}
          </div>
        )}

        {/* Rejection — expected vs received side by side, then the way out. */}
        {error && (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm font-semibold text-red-800">
              {isCover
                ? t('Fichier refusé — la couverture actuelle est inchangée')
                : t('Fichier refusé — l’intérieur actuel est inchangé')}
            </p>
            {error.expected && error.actual && (
              <div className="mt-2.5 grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-white px-3 py-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{t('Attendu')}</span>
                  <div className="text-sm font-bold tabular-nums text-gray-900">{error.expected.wmm} × {error.expected.hmm} mm</div>
                </div>
                <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-red-200">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{t('Reçu')}</span>
                  <div className="text-sm font-bold tabular-nums text-red-700">{error.actual.wmm} × {error.actual.hmm} mm</div>
                </div>
              </div>
            )}
            <p className="mt-2 text-[11px] leading-relaxed text-red-700 whitespace-pre-wrap">{error.message}</p>
            <p className="mt-2 text-[11px] font-medium text-red-800">
              {t('→ Ré-exportez le fichier en {w} × {h} mm, puis réessayez.', { w: expected.wmm, h: expected.hmm })}
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>{t('Annuler')}</Button>
          {/* Interior is a two-step flow: analyse in the browser, show exactly
              what will happen, then let the operator commit. Cover is unchanged. */}
          {!isCover && !prepared ? (
            <Button onClick={analyse} disabled={!file || busy}>
              {busy ? t('Analyse…') : t('Analyser le fichier')}
            </Button>
          ) : (
            <Button onClick={submit} disabled={!file || busy}>
              {busy ? t('En cours…') : (isCover ? t('Remplacer la couverture') : t('Confirmer et remplacer'))}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** The cover file row's replace/restore controls + the "manuelle" badge. */
function CoverOverrideControls({
  shopifyOrderId,
  albumIndex,
  printSettings,
  isOverridden,
  manualFilename,
  manualUploadedAt,
  kind = 'cover',
  manualPages,
  bleedMm,
  introPageUrl,
  outroPageUrl,
  orderLabel,
}: {
  shopifyOrderId: string;
  albumIndex: number;
  printSettings: PrintSettings;
  isOverridden: boolean;
  manualFilename?: string | null;
  manualUploadedAt?: string | null;
  /** Cover and interior are replaced independently, through the same UI. */
  kind?: 'cover' | 'interior';
  manualPages?: number | null;
  /** Interior only — the browser prepare step needs the live bleed setting and
   *  the branding pages it has to insert. */
  bleedMm?: number;
  introPageUrl?: string | null;
  outroPageUrl?: string | null;
  /** Bindery marry-label, identical to the one on the cover ("#1113 1/1"). */
  orderLabel?: string | null;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const isCover = kind === 'cover';

  // Cover → the derived vinyl wrap. Interior → the finished page, which is a
  // fixed 200 × 297 trim (pdfGenerator's PAGE_W / A4_H), not a settings value.
  const expected = isCover
    ? computeVinylSizeMm(withDerivedVinyl(printSettings))
    : { wmm: 200, hmm: 297, bleedMm: bleedMm ?? 3, introPageUrl: introPageUrl ?? null, outroPageUrl: outroPageUrl ?? null, orderLabel: orderLabel ?? null };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });

  async function clearOverride() {
    const msg = isCover
      ? t("Revenir à la couverture générée ? La régénération sera ré-autorisée pour cet album ; la couverture manuelle restera en place jusqu'à la prochaine régénération.")
      : t("Revenir à l’intérieur généré ? La régénération sera ré-autorisée pour cet album ; l’intérieur manuel restera en place jusqu’à la prochaine régénération.");
    if (!confirm(msg)) return;
    setClearing(true);
    try {
      if (isCover) await ordersApi.coverReplaceClear(shopifyOrderId, albumIndex);
      else await ordersApi.interiorReplaceClear(shopifyOrderId, albumIndex);
      refresh();
    } catch (e) {
      alert(t('Échec : {msg}', { msg: (e as Error)?.message ?? '?' }));
    } finally {
      setClearing(false);
    }
  }

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap pl-7">
        {isOverridden && (
          <span
            title={t('Ce fichier a été téléversé manuellement{f}{d}. La régénération — y compris forcée et le contrôle automatique — ne peut pas l\'écraser.', {
              f: manualFilename ? t(' ({name})', { name: manualFilename }) : '',
              d: manualUploadedAt ? t(' le {date}', { date: formatDate(manualUploadedAt) }) : '',
            })}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-50 text-violet-700 border border-violet-200"
          >
            <span aria-hidden>🔒</span>
            {isCover
              ? t('Couverture manuelle')
              : t('Intérieur manuel{p}', { p: manualPages ? t(' · {n} pages', { n: manualPages }) : '' })}
          </span>
        )}
        <button
          onClick={() => setOpen(true)}
          title={isCover
            ? t('Téléverser une couverture de remplacement ({w} × {h} mm). Remplace uniquement la couverture — intérieur et mise en page inchangés.', { w: expected.wmm, h: expected.hmm })
            : t('Téléverser un intérieur de remplacement (pages de {w} × {h} mm). Remplace uniquement l’intérieur — la couverture reste inchangée.', { w: expected.wmm, h: expected.hmm })}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-brand-300 transition-colors"
        >
          <span aria-hidden>📤</span>
          {isCover ? t('Remplacer la couverture') : t('Remplacer l’intérieur')}
        </button>
        {isOverridden && (
          <button
            onClick={clearOverride}
            disabled={clearing}
            title={t('Supprime le verrou et ré-autorise la régénération automatique de ce fichier.')}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <span aria-hidden>↩︎</span>
            {clearing ? t('…') : (isCover ? t('Revenir à la couverture générée') : t('Revenir à l’intérieur généré'))}
          </button>
        )}
      </div>
      {open && (
        <CoverReplaceDialog
          shopifyOrderId={shopifyOrderId}
          albumIndex={albumIndex}
          expected={expected}
          kind={kind}
          onClose={() => setOpen(false)}
          onDone={refresh}
        />
      )}
    </>
  );
}

// ─── Missing-originals resolution actions (2026-07-11) ───────────────────────
// Lives inside the red "Impression bloquée" banner. Two EXPLICIT admin tools —
// nothing here is automatic, and nothing here notifies the client:
//   1. « Forcer le PDF » — substitutes the ~1600px display copies for the
//      missing originals, writes the permanent force_print_log, then fires the
//      server render. Confirm dialog first; the client never sees anything.
//   2. « Demander re-téléversement » — opens WhatsApp ON THE ADMIN'S SCREEN
//      with a prefilled message listing the exact files + the magic upload
//      link (minted silently when absent). The ADMIN presses send.
function MissingOriginalsActions({ order, missing }: {
  order: Order;
  missing: { session_id: string; album_index: number; photo_id: string; original_filename: string | null; has_thumbnail: boolean }[];
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [waBusy, setWaBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forced, setForced] = useState<{ substituted_count: number; removed_count: number } | null>(null);

  const substitutable = missing.filter((m) => m.has_thumbnail).length;
  const nothingAtAll = missing.length - substitutable;

  const forceMutation = useMutation({
    mutationFn: () => ordersApi.forcePrintFallback(order.shopify_order_id),
    onSuccess: (r) => {
      setConfirming(false);
      setError(null);
      setForced({ substituted_count: r.substituted_count, removed_count: r.removed_count });
      queryClient.invalidateQueries({ queryKey: ['order', order.shopify_order_id] });
      queryClient.invalidateQueries({ queryKey: ['print-integrity', order.shopify_order_id] });
    },
    onError: (e: Error) => setError(e.message),
  });

  // Manual WhatsApp compose — ensure an active magic upload link (mint
  // silently if needed), then open wa.me prefilled. Never auto-sends.
  async function composeWhatsApp() {
    if (!order.customer_phone) { setError(t('Pas de numéro de téléphone sur cette commande.')); return; }
    setWaBusy(true);
    setError(null);
    try {
      const now = Date.now();
      const tokenActive = !!order.upload_token
        && !order.upload_token_revoked_at
        && (!order.upload_token_expires_at || new Date(order.upload_token_expires_at).getTime() > now);
      let url = tokenActive ? `${window.location.origin}/upload/${order.upload_token}` : null;
      if (!url) {
        const minted = await uploadLinkApi.generate(order.shopify_order_id);
        url = minted.upload_url || `${window.location.origin}/upload/${minted.token}`;
        queryClient.invalidateQueries({ queryKey: ['order', order.shopify_order_id] });
      }
      const digits = order.customer_phone.replace(/\D/g, '');
      const intl = digits.startsWith('212') ? digits : digits.startsWith('0') ? '212' + digits.slice(1) : digits;
      const firstName = (order.customer_name ?? '').split(' ')[0] || '';
      const names = missing.map((m) => m.original_filename).filter((n): n is string => !!n);
      const shown = names.slice(0, 15);
      const text = `Bonjour ${firstName} 👋

Petit souci technique : ${missing.length} photo${missing.length > 1 ? 's' : ''} de votre album ne sont pas arrivées en pleine qualité sur nos serveurs :
${shown.map((n) => `• ${n}`).join('\n')}${names.length > shown.length ? `\n… et ${names.length - shown.length} autres` : ''}

Pour garantir une impression parfaite, merci de les renvoyer via votre lien sécurisé :
${url}

Vos autres photos et votre mise en page sont bien conservées 👍`;
      window.open(`https://wa.me/${intl}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setWaBusy(false);
    }
  }

  return (
    <div className="mt-2.5">
      {error && (
        <p className="text-xs text-red-700 bg-red-100 border border-red-200 rounded-lg px-2.5 py-1.5 mb-2 break-words">{error}</p>
      )}
      {forced ? (
        <p className="text-xs font-medium text-green-800 bg-green-50 border border-green-200 rounded-lg px-2.5 py-1.5">
          ✓ {t('{n} photo(s) substituée(s) — PDF en cours de génération côté serveur…', { n: forced.substituted_count })}
        </p>
      ) : (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setConfirming(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
          >
            ⚙️ {t('Forcer le PDF avec les copies disponibles')}
          </button>
          <button
            onClick={composeWhatsApp}
            disabled={waBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#25D366] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            📲 {waBusy ? t('Préparation…') : t('Demander re-téléversement (WhatsApp)')}
          </button>
        </div>
      )}

      <Modal isOpen={confirming} onClose={() => setConfirming(false)} title={t('Forcer le PDF — qualité réduite')} size="md">
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            {t('{n} photo(s) seront imprimées depuis leur copie d’affichage (~1600px) au lieu de l’original pleine qualité.', { n: substitutable })}
          </p>
          {nothingAtAll > 0 && (
            <p className="text-sm text-gray-700">
              {t('{n} photo(s) sans aucune copie seront retirées (elles ne figurent pas dans l’album).', { n: nothingAtAll })}
            </p>
          )}
          <ul className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 max-h-36 overflow-y-auto space-y-0.5">
            {missing.filter((m) => m.has_thumbnail).map((m) => (
              <li key={m.photo_id}>• {m.original_filename ?? m.photo_id.slice(0, 8)} <span className="text-gray-400">(album {m.album_index})</span></li>
            ))}
          </ul>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            {t('Action définitive et tracée : la liste exacte des photos concernées est enregistrée sur la commande. Le client n’est pas notifié.')}
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>{t('Annuler')}</Button>
            <Button
              variant="primary"
              size="sm"
              loading={forceMutation.isPending}
              onClick={() => forceMutation.mutate()}
            >
              {t('Confirmer et générer le PDF')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Force-print trail strip ─────────────────────────────────────────────────
// Renders orders.force_print_log — the permanent record of what shipped below
// print spec. Stays visible forever (support/QA reference), expandable.
function ForcePrintLogStrip({ log }: {
  log: NonNullable<Order['force_print_log']>;
}) {
  const { t } = useT();
  return (
    <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-200">
      {log.map((entry, i) => (
        <details key={i} className="text-xs text-amber-800">
          <summary className="cursor-pointer font-medium select-none">
            ⚠️ {t('Forcé en qualité réduite le {d} — {n} photo(s) en copie d’affichage', {
              d: formatDate(entry.at),
              n: entry.substituted.length,
            })}
            {entry.removed.length > 0 ? ` · ${t('{n} vignette(s) vide(s) retirée(s)', { n: entry.removed.length })}` : ''}
          </summary>
          <ul className="mt-1.5 ml-4 space-y-0.5 text-amber-700 max-h-32 overflow-y-auto">
            {entry.substituted.map((s) => (
              <li key={s.photo_id}>• {s.filename ?? s.photo_id.slice(0, 8)} <span className="opacity-60">(album {s.album_index})</span></li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

// ─── PDF Generation Card ─────────────────────────────────────────────────────

function PdfGenerationCard({
  order,
}: {
  order: Order;
  shopifyOrderId: string;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  // « Régénérer » rebuilds on the server — no editor, no « Confirmer » (#1241).
  // The order query polls while pdf_status is 'generating' and then while the
  // 3D book rebuilds, so the card walks to the new version on its own.
  const [regenNote, setRegenNote] = useState<string | null>(null);
  // The live bars (2026-10-21): shown from the click until both halves settle.
  const [regenAt, setRegenAt] = useState(0);
  const regenerate = useMutation({
    mutationFn: () => ordersApi.pdfRegenerate(order.shopify_order_id),
    onSuccess: (r) => {
      setRegenAt(Date.now());
      setRegenNote(r.already
        ? t('Déjà en cours — le nouveau PDF arrive, puis l’aperçu 3D.')
        : r.quick?.runner
          ? t('Lancé — l’aperçu 3D se met à jour en quelques secondes, le PDF d’impression suit.')
          : t('Lancé — le PDF puis l’aperçu 3D se refont tout seuls (1 à 3 min).'));
      void queryClient.invalidateQueries({ queryKey: ['order', order.shopify_order_id] });
    },
    onError: (e) => setRegenNote(e instanceof Error ? e.message : t('Impossible de lancer la régénération')),
  });
  // Global print/assembly settings for the fiche de montage (single app_settings
  // row → applies to every order). React Query dedupes with the settings page.
  const { data: settingsData } = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get() });
  const printSettings = { ...DEFAULT_PRINT_SETTINGS, ...(settingsData?.settings?.print_template_settings ?? {}) };

  // Fix 4 — server-side byte-check of every original for this order (one RPC
  // per album). Drives the "impression bloquée" banner below.
  const { data: printIntegrity } = useQuery({
    queryKey: ['print-integrity', order.shopify_order_id],
    queryFn: () => ordersApi.printIntegrity(order.shopify_order_id),
    staleTime: 60_000,
  });

  // Per-album PDFs are the authoritative source for Duo/Trio. For Solo
  // orders the legacy orders.{cover,interior}_pdf_url columns hold the
  // single album's files; we fall back to those if album_pdfs is empty.
  const albumPdfs = order.album_pdfs ?? [];
  const legacyEntry = (order.cover_pdf_url || order.interior_pdf_url || order.full_book_pdf_url)
    ? {
        album_index: 1,
        destination: order.destination_1 ?? null,
        cover_pdf_url:     order.cover_pdf_url,
        interior_pdf_url:  order.interior_pdf_url,
        full_book_pdf_url: order.full_book_pdf_url,
        // Legacy fallback only fires when there is no album_sessions row to read
        // the override from, so it is definitionally not overridden.
        cover_manual_override: false,
        cover_manual_uploaded_at: null as string | null,
        cover_manual_filename: null as string | null,
        interior_manual_override: false,
        interior_manual_uploaded_at: null as string | null,
        interior_manual_filename: null as string | null,
        interior_manual_pages: null as number | null,
      }
    : null;
  const pdfRows = albumPdfs.length > 0
    ? albumPdfs
    : (legacyEntry ? [legacyEntry] : []);
  const hasAnyPdf = pdfRows.some((r) => r.cover_pdf_url || r.interior_pdf_url || r.full_book_pdf_url);

  // Admin one-click regenerate. The editor's LAST-album Confirmer already
  // renders PDFs for every album in the order (Solo = 1, Duo = 2, Trio = 3),
  // so we open the editor at the last album and the admin just needs to
  // click Confirmer there. New tab so the original order page stays open
  // for cross-reference. Disabled when PDFs aren't ready (no sessions to
  // regenerate from) OR while a regen is already in-flight.
  const BUNDLE_QTY: Record<string, number> = { Solo: 1, Duo: 2, Trio: 3 };
  const lastAlbumIndex = BUNDLE_QTY[order.bundle ?? 'Solo'] ?? 1;
  const regenUrl = `/editor/${order.shopify_order_id}/${lastAlbumIndex}/edit`;
  const canRegen = !!order.album_submitted_at && order.pdf_status !== 'generating';

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-gray-800">{t('Génération PDF')}</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {order.pdf_status === 'ready'
              ? t('Couverture A3+ (dos + tranche + couverture) + Intérieur + Livre complet')
              : t('Couverture A3+ + Intérieur mis en page')}
          </p>
          {order.album_submitted_at && (
            <p className="text-[11px] text-green-600 mt-1 font-medium">
              {t('✓ Version finale validée le {date} — ces fichiers reflètent la dernière soumission du client', { date: formatDate(order.album_submitted_at) })}
            </p>
          )}
        </div>
        {/* flex-wrap: this group holds three unshrinkable siblings (Régénérer,
            the RELIEF 3D badge, PdfStatusBadge whose longest state reads
            "Génération en cours…"). The parent wraps, but without this the group
            overflowed its own line on a phone. */}
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          {canRegen && (
            <button
              type="button"
              onClick={() => regenerate.mutate()}
              disabled={regenerate.isPending}
              title={t('Reconstruit sur nos serveurs les PDFs de tous les albums (couverture + intérieur), puis l’aperçu 3D. Aucun message n’est envoyé au client.')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-brand-300 transition-colors disabled:opacity-50 disabled:cursor-wait"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {regenerate.isPending ? t('Envoi…') : t('Régénérer')}
            </button>
          )}
          {!!order.album_submitted_at && (
            <a
              href={regenUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={t("Ouvre l'éditeur sur l'album {n}", { n: lastAlbumIndex })}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors"
            >
              {t('Éditeur')}
            </a>
          )}
          {/* Production instruction. Deliberately on the PDF card — this is the
              card the operator is looking at when they hand the job to print. */}
          {order.relief_3d && (
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 text-[11px] font-bold"
              title={t('Couverture Relief 3D payée — la couverture de chaque album doit être gaufrée en relief')}
            >
              ✦ {t('RELIEF 3D')}
            </span>
          )}
          <PdfStatusBadge status={order.pdf_status} />
        </div>
      </div>

      {regenNote && (
        <div className="px-5 py-2 bg-gray-50 border-b border-gray-100" role="status">
          <p className="text-xs text-gray-700">{regenNote}</p>
          {regenAt > 0 && (
            <div className="mt-2 max-w-md">
              <QuickBuildProgress shopifyOrderId={order.shopify_order_id} since={regenAt} compact />
            </div>
          )}
        </div>
      )}

      {order.pdf_status === 'generating' && (
        <div className="px-5 py-3 bg-amber-50 border-b border-amber-100">
          <p className="text-sm text-amber-700 font-medium">
            {t('⏱ Nos serveurs génèrent les PDFs en arrière-plan. Le client n\'a pas besoin de rester connecté — un contrôle automatique relance la génération si elle bloque.')}
          </p>
        </div>
      )}

      {order.pdf_status === 'failed' && (
        <div className="px-5 py-3 bg-red-50 border-b border-red-100">
          <p className="text-sm text-red-700 font-medium">
            {t('La génération serveur a échoué. Cliquez « Régénérer » ci-dessous pour relancer.')}
          </p>
          {order.pdf_render_error && (
            <p className="mt-1 text-xs text-red-600/90 break-words">{order.pdf_render_error}</p>
          )}
        </div>
      )}

      {/* Fix 4 — print-integrity warnings (policy 2026-07-13). Two tiers:
          RED  = truly blocked: photo(s) PLACED on a page with NO printable
                 copy at all — submit/render refuse until replaced/removed.
          AMBER = degraded: originals missing but a thumbnail/preview exists —
                 the order prints that copy at reduced quality (~512px). Purely
                 informational so the admin can eyeball the PDF / warn the
                 client; nothing is blocked. */}
      {printIntegrity && !printIntegrity.unavailable && (printIntegrity.blocking?.length ?? 0) > 0 && (
        <div className="px-5 py-3 bg-red-50 border-b border-red-200">
          <p className="text-sm text-red-700 font-semibold">
            🚫 {t('Impression bloquée — {n} photo(s) placée(s) sans aucune copie imprimable', { n: printIntegrity.blocking.length })}
          </p>
          <p className="text-xs text-red-600 mt-1">
            {t('À remplacer ou retirer de la page :')}{' '}
            {printIntegrity.blocking.slice(0, 8).map((m) => m.original_filename ?? m.photo_id.slice(0, 8)).join(', ')}
            {printIntegrity.blocking.length > 8 ? ` … +${printIntegrity.blocking.length - 8}` : ''}
          </p>
          <MissingOriginalsActions order={order} missing={printIntegrity.blocking} />
        </div>
      )}
      {printIntegrity && !printIntegrity.unavailable && (printIntegrity.blocking?.length ?? 0) === 0 && (printIntegrity.degraded?.length ?? 0) > 0 && (
        <div className="px-5 py-3 bg-amber-50 border-b border-amber-200">
          <p className="text-sm text-amber-800 font-semibold">
            ⚠️ {t('Qualité réduite — {n} photo(s) imprimée(s) depuis la miniature (~512px)', { n: printIntegrity.degraded.length })}
          </p>
          <p className="text-xs text-amber-700 mt-1">
            {printIntegrity.degraded.slice(0, 8).map((m) => m.original_filename ?? m.photo_id.slice(0, 8)).join(', ')}
            {printIntegrity.degraded.length > 8 ? ` … +${printIntegrity.degraded.length - 8}` : ''}
            {' — '}{t('vérifiez le rendu PDF avant impression.')}
          </p>
        </div>
      )}

      {/* Permanent force-print trail (migration 060) — visible even after the
          gate clears, so support can always see what shipped below spec. */}
      {Array.isArray(order.force_print_log) && order.force_print_log.length > 0 && (
        <ForcePrintLogStrip log={order.force_print_log} />
      )}

      {/* Stale-PDF warning — session edited after last PDF generation.
          PDFs only re-render on submit, so a client (or admin) editing the
          album after submit leaves the cover.pdf / interior.pdf frozen in
          the previous design until the next Commander click. */}
      {(() => {
        const sessionAt = order.session_last_saved_at ? Date.parse(order.session_last_saved_at) : 0;
        const pdfAt = order.cover_pdf_updated_at ? Date.parse(order.cover_pdf_updated_at) : 0;
        const stale = hasAnyPdf && sessionAt > 0 && pdfAt > 0 && sessionAt > pdfAt + 30_000; // 30s grace
        // The 3D preview is rasterized FROM the PDFs, so it goes stale with
        // them — and it is the one the CLIENT sees. #D361 was printed from a
        // file 8.6 h older than the album: the photos had visibly moved, the
        // editor was right, and nothing on this page said the preview was old
        // too. Track both, and say plainly what is affected.
        const prevAt = order.previews_generated_at ? Date.parse(order.previews_generated_at) : 0;
        const previewStale = sessionAt > 0 && prevAt > 0 && sessionAt > prevAt + 30_000;
        if (!stale && !previewStale) return null;
        const both = stale && previewStale;
        return (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100" role="alert">
            <p className="text-sm text-amber-800 font-medium">
              {both
                ? t("⚠ Les PDFs ET l'aperçu 3D du client sont antérieurs à la dernière modification de l'album.")
                : stale
                  ? t('⚠ Les PDFs ci-dessous sont antérieurs à la dernière modification du client.')
                  : t("⚠ L'aperçu 3D envoyé au client est antérieur à la dernière modification de l'album.")}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              {t('Dernière modification : {mod}.', { mod: formatDate(order.session_last_saved_at!) })}{' '}
              {stale && order.cover_pdf_updated_at
                ? t('PDFs générés : {gen}.', { gen: formatDate(order.cover_pdf_updated_at) }) : ''}{' '}
              {previewStale && order.previews_generated_at
                ? t('Aperçu 3D généré : {gen}.', { gen: formatDate(order.previews_generated_at) }) : ''}
            </p>
            <p className="text-xs text-amber-800 mt-1 font-medium">
              {t("→ Ne pas imprimer ni envoyer le lien client avant d'avoir régénéré : ouvrir l'éditeur, cliquer sur Commander.")}
            </p>
          </div>
        );
      })()}

      {/* Admin print-verification tools — ALWAYS available. Preview the cover
          (with assembly guides) + grab the universal fiche WITHOUT submitting or
          changing order status. Cover preview reads the album's current session,
          so it works before AND after submission. */}
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
        <FicheMontageButton settings={printSettings} />
        {Array.from({ length: lastAlbumIndex }, (_, i) => i + 1).map((n) => (
          <CoverPreviewButton
            key={n}
            shopifyOrderId={order.shopify_order_id}
            albumIndex={n}
            albumCount={lastAlbumIndex}
            settings={settingsData?.settings}
            // This preview re-renders from the album's LIVE SESSION and never
            // reads the stored PDF, so with a manual cover in force it would
            // show the GENERATED cover — i.e. not what will be printed. Flag it
            // rather than silently handing the operator a misleading file.
            coverOverridden={pdfRows.some((r) => r.album_index === n && r.cover_manual_override)}
          />
        ))}
        <span className="text-xs text-gray-400">{t('Aperçu non destructif — ne soumet pas, ne change pas le statut.')}</span>
      </div>

      {/* PDF file list — one block per album. Solo shows a single block
          unlabelled; Duo/Trio show "Album N · {destination}" headers above
          each block so the admin can tell which file belongs to which album.
          Spine size shown per album so the print shop can pre-cut the cover
          stock. Page count is currently the order-level value (same for every
          album); when per-album page counts ship, swap in r.page_count here. */}
      {hasAnyPdf && (
        <div className="px-5 py-4 space-y-4">
          {pdfRows.map((r) => {
            const hasAny = !!(r.cover_pdf_url || r.interior_pdf_url || r.full_book_pdf_url);
            if (!hasAny) return null;
            const showHeader = pdfRows.length > 1;
            return (
              <div key={r.album_index} className={showHeader ? 'space-y-2' : 'space-y-2'}>
                {showHeader && (
                  <div className="flex items-baseline gap-2 pb-1 border-b border-gray-100">
                    <span className="text-xs font-semibold text-gray-700">{t('Album {n}', { n: r.album_index })}</span>
                    {r.destination && (
                      <span className="text-xs text-gray-500">· {r.destination}</span>
                    )}
                  </div>
                )}
                <SpineInfo pageCount={order.page_count} themed={(r as { themed_book?: boolean }).themed_book === true} />
                {r.cover_pdf_url && (
                  <>
                    <PdfFileRow
                      icon={r.cover_manual_override ? '🔒' : '📄'}
                      label={r.cover_manual_override
                        ? t('Couverture A3+ — remplacée manuellement')
                        : t('Couverture A3+ (dos + tranche + couv. avant)')}
                      url={r.cover_pdf_url}
                    />
                    <CoverOverrideControls
                      shopifyOrderId={order.shopify_order_id}
                      albumIndex={r.album_index}
                      printSettings={printSettings}
                      isOverridden={r.cover_manual_override === true}
                      manualFilename={r.cover_manual_filename}
                      manualUploadedAt={r.cover_manual_uploaded_at}
                    />
                  </>
                )}
                {r.interior_pdf_url && (
                  <>
                    <PdfFileRow
                      icon={r.interior_manual_override ? '🔒' : '📑'}
                      label={r.interior_manual_override
                        ? t('Intérieur PDF — remplacé manuellement')
                        : t('Intérieur PDF')}
                      url={r.interior_pdf_url}
                    />
                    <CoverOverrideControls
                      kind="interior"
                      shopifyOrderId={order.shopify_order_id}
                      albumIndex={r.album_index}
                      printSettings={printSettings}
                      isOverridden={r.interior_manual_override === true}
                      manualFilename={r.interior_manual_filename}
                      manualUploadedAt={r.interior_manual_uploaded_at}
                      manualPages={r.interior_manual_pages}
                      orderLabel={order.shopify_order_name
                        ? `${order.shopify_order_name} ${r.album_index}/${({ Solo: 1, Duo: 2, Trio: 3 } as Record<string, number>)[order.bundle ?? 'Solo'] ?? 1}`
                        : null}
                      bleedMm={settingsData?.settings?.pdf_bleed_mm ?? 3}
                      introPageUrl={settingsData?.settings?.intro_page_1_url ?? null}
                      outroPageUrl={settingsData?.settings?.outro_page_2_url ?? null}
                    />
                  </>
                )}
                {r.full_book_pdf_url && r.full_book_pdf_url !== r.interior_pdf_url && (
                  <PdfFileRow icon="📚" label={t('Livre complet PDF')} url={r.full_book_pdf_url} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Show spine size even when no PDFs are ready yet — admin needs the
          measurement for the print shop regardless of file readiness. */}
      {!hasAnyPdf && order.page_count > 0 && (
        <div className="px-5 py-4">
          <SpineInfo pageCount={order.page_count} themed={albumPdfs[0]?.themed_book === true} />
        </div>
      )}

      {/* Info: PDFs are generated client-side at full quality. Admin regen
          requires loading the editor as the customer (until the external
          rendering service ships). */}
      <div className="px-5 py-4 bg-gray-50 space-y-2">
        <p className="text-xs text-gray-500">
          {t("Les PDFs sont générés en pleine qualité par nos serveurs, en arrière-plan, dès la soumission — sans dépendre du navigateur du client. Pour ajuster: ouvrez l'éditeur, modifiez, puis « Régénérer ».")}
        </p>
        {order.pdf_status === 'ready' && (
          <span className="text-xs text-green-600 font-medium">{t('✓ PDFs disponibles')}</span>
        )}
        {order.pdf_status === 'failed' && (
          <span className="text-xs text-red-600 font-medium">
            {t('Dernière génération échouée — demandez au client de re-soumettre')}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Flipbook Preview Link Card ──────────────────────────────────────────────
// Client-facing 3D flipbook at /preview/:token — the client reviews their
// album, leaves per-page notes and confirms before print. Same token model
// as the upload/resume links. Preview JPEGs are rasterized from the print
// PDFs: automatically on every customer submit / admin regen (multiAlbumPdf
// hook), or on demand here via the backfill button (admin browser fetches
// the stored PDFs and renders the previews itself — no server rendering).

const REVIEW_STATUS_META: Record<string, { label: string; cls: string }> = {
  approved:          { label: 'Prêt à imprimer',          cls: 'text-green-700 bg-green-50 border-green-200' },
  changes_requested: { label: 'Modifications demandées',  cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  pending:           { label: 'En attente du client',     cls: 'text-gray-600 bg-gray-50 border-gray-200' },
};

/**
 * « Approve and send » (owner, 2026-10-08): the build on screen waits for the
 * manager — the same door as his WhatsApp « OK » (worker previewApprovalRun.ts).
 * Shown only while it waits, or while the quality check holds it back.
 */
function PreviewApprovalBanner({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  // What the click answered — the worker says what happens next (2026-10-15,
  // #WA-510802: at 01:08 this said « 5 minutes » and the night hold kept it).
  const [note, setNote] = useState<{ text: string; held: boolean } | null>(null);
  const approve = useMutation({
    mutationFn: () => pilotageApi.approvePreview(shopifyOrderId),
    onSuccess: (d) => {
      const plan = d.sends ? (d as PreviewPlanLike) : null;
      setNote({ text: `${t('Approuvé —')} ${previewPlanText(plan, t)}`, held: planIsHeld(plan) });
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['preview-send-plan', shopifyOrderId] });
    },
    onError: (e: Error) => setNote({ text: e.message, held: true }),
  });
  const state = order.preview_approval_state;
  // Approved, link not out yet: the banner STAYS and says when it leaves (or
  // why it will not) — after a reload too, not only in the click's answer.
  const approvedUnsent = state === 'approved' && !order.preview_link_sent_at;
  const planQ = useQuery({
    queryKey: ['preview-send-plan', shopifyOrderId],
    queryFn: () => pilotageApi.previewSendPlan(shopifyOrderId),
    enabled: approvedUnsent,
    refetchInterval: 60_000,
  });
  if (order.preview_link_sent_at) return null;
  if (approvedUnsent) {
    const plan = planQ.data?.plan ?? null;
    const held = plan ? planIsHeld(plan) : Boolean(note?.held);
    return (
      <div role="status" className={`mb-4 rounded-lg border px-3.5 py-3 text-[13px] ${held ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20' : 'border-green-200 bg-green-50 dark:bg-green-900/20'}`}>
        <p className="font-semibold text-gray-800 dark:text-[#F3EBDD]">
          {plan ? `${t('Aperçu 3D approuvé —')} ${previewPlanText(plan, t)}`
            : note ? note.text
              : planQ.isError ? `${t('Aperçu 3D approuvé —')} ${t('impossible de vérifier quand il part.')}`
                : `${t('Aperçu 3D approuvé —')} ${t('vérification…')}`}
        </p>
      </div>
    );
  }
  if (state !== 'waiting' && state !== 'failed') return null;
  const problems = (order.preview_approval_check?.problems ?? []).map((p) => p.what).filter(Boolean) as string[];
  return (
    <div className={`mb-4 rounded-lg border px-3.5 py-3 text-[13px] ${state === 'waiting' ? 'border-brand-300 bg-brand-50 dark:bg-brand-900/20' : 'border-amber-300 bg-amber-50 dark:bg-amber-900/20'}`}>
      <p className="font-semibold text-gray-800 dark:text-[#F3EBDD]">
        {state === 'waiting' ? t('Aperçu 3D à approuver — rien n’est envoyé à la cliente avant votre OK.') : t('Aperçu 3D pas prêt — le contrôle qualité le retient.')}
      </p>
      {state === 'failed' && problems.length > 0 && (
        <ul className="mt-1 list-disc pl-5 text-gray-700 dark:text-[#E8DFD0]">{problems.slice(0, 5).map((p) => <li key={p}>{p}</li>)}</ul>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => approve.mutate()} disabled={approve.isPending}
          className="h-9 px-3.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-semibold cursor-pointer disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          ✓ {state === 'waiting' ? t('Approuver et envoyer') : t('Approuver quand même et envoyer')}
        </button>
        {note && <span role="status" className={note.held ? 'font-medium text-amber-800 dark:text-amber-300' : 'text-gray-600 dark:text-[#E8DFD0]'}>{note.text}</span>}
      </div>
    </div>
  );
}

function PreviewLinkCard({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState<string | null>(null);

  // Print settings drive the cover-wrap crop geometry (same source as the
  // fiche de montage) — React Query dedupes with the other cards.
  const { data: settingsData } = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get() });
  const printSettings = { ...DEFAULT_PRINT_SETTINGS, ...(settingsData?.settings?.print_template_settings ?? {}) };

  const { data: review, refetch } = useQuery({
    queryKey: ['preview-review', shopifyOrderId],
    queryFn: () => previewLinkApi.adminNotes(shopifyOrderId),
  });

  const generateMutation = useMutation({
    mutationFn: () => previewLinkApi.generate(shopifyOrderId),
    onSuccess: () => { setError(null); refetch(); },
    onError: (e: Error) => setError(e.message),
  });
  const revokeMutation = useMutation({
    mutationFn: () => previewLinkApi.revoke(shopifyOrderId),
    onSuccess: () => { setError(null); refetch(); },
    onError: (e: Error) => setError(e.message),
  });
  const resolveMutation = useMutation({
    mutationFn: () => previewLinkApi.resolveNotes(shopifyOrderId),
    onSuccess: () => { setError(null); refetch(); },
    onError: (e: Error) => setError(e.message),
  });

  const previewUrl = review?.preview_url ?? null;
  const statusMeta = review?.client_review_status
    ? REVIEW_STATUS_META[review.client_review_status]
    : null;

  const openNotes = (review?.notes ?? []).filter((n: PreviewNote) => !n.resolved_at);
  const resolvedCount = (review?.notes ?? []).length - openNotes.length;

  // Per-album print PDFs — same source rows as PdfGenerationCard.
  const albumPdfRows = (order.album_pdfs ?? []).length > 0
    ? (order.album_pdfs ?? [])
    : ((order.cover_pdf_url && order.interior_pdf_url)
        ? [{ album_index: 1, destination: order.destination_1 ?? null,
             cover_pdf_url: order.cover_pdf_url, interior_pdf_url: order.interior_pdf_url,
             full_book_pdf_url: order.full_book_pdf_url }]
        : []);
  const backfillRows = albumPdfRows.filter((r) => r.cover_pdf_url && r.interior_pdf_url);

  async function copyLink() {
    if (!previewUrl) return;
    try {
      await navigator.clipboard.writeText(previewUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  }

  function whatsappUrl(): string | null {
    if (!previewUrl || !order.customer_phone) return null;
    const digits = order.customer_phone.replace(/\D/g, '');
    const intl = digits.startsWith('212') ? digits :
                 digits.startsWith('0') ? '212' + digits.slice(1) :
                 digits;
    const firstName = (order.customer_name ?? '').split(' ')[0] || '';
    const text = `Bonjour ${firstName} 👋

Votre album Storiz est prêt ! Feuilletez-le en avant-première ici :
${previewUrl}

Si tout vous plaît, confirmez directement dans la page. Vous pouvez aussi laisser une note sur n'importe quelle page 📖✨`;
    return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
  }

  // SERVER previews (2026-07-08): the button fires the worker's headless
  // preview render (same rasterize pipeline, on the server) instead of
  // grinding pdfjs in THIS browser tab. Fire-and-poll: previews_status on the
  // order flips generating → ready|failed; poll the order query while it runs.
  const queryClient = useQueryClient();
  const serverPreviews = useMutation({
    mutationFn: () => ordersApi.generatePreviewsServer(shopifyOrderId),
    onSuccess: () => {
      setError(null);
      setBackfillMsg(t('Génération des aperçus sur le serveur…'));
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    },
    onError: (e: Error) => setError(e.message),
  });
  const previewsGenerating = order.previews_status === 'generating' || (serverPreviews.isSuccess && !order.previews_status);
  useEffect(() => {
    if (order.previews_status !== 'generating') return;
    const iv = setInterval(
      () => queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] }),
      4000,
    );
    return () => clearInterval(iv);
  }, [order.previews_status, shopifyOrderId, queryClient]);
  useEffect(() => {
    if (order.previews_status === 'ready' && backfillMsg) {
      setBackfillMsg(t('✓ Aperçus générés sur le serveur'));
    } else if (order.previews_status === 'failed' && backfillMsg) {
      setBackfillMsg(null);
      setError(order.previews_error ?? t('Génération des aperçus échouée'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.previews_status]);
  void backfillBusy; void setBackfillBusy; void printSettings; // legacy in-browser path retired

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <PreviewApprovalBanner order={order} shopifyOrderId={shopifyOrderId} />
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-700">{t('Aperçu client — flipbook 3D')}</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {t('Le client feuillette son album, laisse ses notes page par page et confirme avant impression.')}
          </p>
        </div>
        {statusMeta && (
          <span className={`text-xs font-medium border rounded-full px-2.5 py-0.5 whitespace-nowrap ${statusMeta.cls}`}>
            {t(statusMeta.label)}
            {review?.client_review_at && review.client_review_status !== 'pending' && (
              <span className="font-normal opacity-70"> · {formatDate(review.client_review_at)}</span>
            )}
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mb-3">
          {error}
        </div>
      )}

      {previewUrl ? (
        <>
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2 mb-3">
            <input
              readOnly
              value={previewUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 bg-transparent text-sm text-gray-700 font-mono outline-none truncate"
            />
            <Button size="sm" variant="secondary" onClick={copyLink}>
              {copied ? t('✓ Copié') : t('Copier')}
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap mb-1">
            {whatsappUrl() && (
              <a
                href={whatsappUrl()!}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#25D366] text-white text-sm font-medium hover:opacity-90"
              >
                WhatsApp
              </a>
            )}
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('Ouvrir ↗')}
            </a>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => serverPreviews.mutate()}
              loading={serverPreviews.isPending || previewsGenerating}
              disabled={backfillRows.length === 0}
              title={backfillRows.length === 0
                ? t("Aucun PDF disponible — régénérez d'abord les PDFs.")
                : t('Re-rasterise les aperçus depuis les PDFs stockés — sur le serveur, votre navigateur reste libre.')}
            >
              {t('Générer les aperçus')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => revokeMutation.mutate()}
              loading={revokeMutation.isPending}
            >
              {t('Révoquer')}
            </Button>
          </div>
          {backfillMsg && (
            <p className="text-xs text-gray-500 mt-2">{backfillMsg}</p>
          )}
        </>
      ) : (
        <div className="flex gap-2 flex-wrap items-center">
          <Button
            size="sm"
            onClick={() => generateMutation.mutate()}
            loading={generateMutation.isPending}
            disabled={backfillRows.length === 0}
          >
            {t("Générer le lien d'aperçu")}
          </Button>
          {backfillRows.length === 0 && (
            <span className="text-xs text-gray-400">
              {t('Disponible une fois les PDFs générés.')}
            </span>
          )}
        </div>
      )}

      {/* Client notes — the heart of the review loop. */}
      {openNotes.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-amber-700">
              {t('Notes du client ({n})', { n: openNotes.length })}
            </h3>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => resolveMutation.mutate()}
              loading={resolveMutation.isPending}
              title={t("Marque toutes les notes comme corrigées et repasse l'aperçu en attente — le client pourra re-confirmer.")}
            >
              {t('Marquer comme corrigées')}
            </Button>
          </div>
          {/* One by one, with who did what: « Demandes du client » above
              (2026-10-03) — one list, never two copies of the same notes. */}
          <p className="text-xs text-gray-500">
            {t('Une par une, avec qui a fait quoi : « Demandes du client » ci-dessus.')}
          </p>
        </div>
      )}
      {openNotes.length === 0 && resolvedCount > 0 && (
        <p className="text-xs text-gray-400 mt-3">
          {resolvedCount > 1
            ? t('✓ {n} notes corrigées (historique conservé).', { n: resolvedCount })
            : t('✓ {n} note corrigée (historique conservé).', { n: resolvedCount })}
        </p>
      )}
    </div>
  );
}

// ─── Redo / Réimpression Card ─────────────────────────────────────────────────

const REDO_SCOPE_LABELS: Record<RedoScope, string> = {
  full: 'Album complet (couv. + intérieur)',
  'cover-only': 'Couverture uniquement',
  'interior-only': 'Intérieur uniquement',
  'custom-pages': 'Pages spécifiques',
};
const REDO_REASON_LABELS: Record<RedoReason, string> = {
  'print-error': 'Erreur d\'impression',
  'client-request': 'Demande client',
  'quality-issue': 'Problème de qualité',
  other: 'Autre',
};
const REDO_STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  generating: 'Génération…',
  ready: 'Prêt',
  'sent-to-printer': 'Envoyé à l\'imprimeur',
  completed: 'Terminé',
  cancelled: 'Annulé',
};

function RedoStatusBadge({ status }: { status: string }) {
  const { t } = useT();
  const tone: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-700',
    generating: 'bg-amber-100 text-amber-700',
    ready: 'bg-blue-100 text-blue-700',
    'sent-to-printer': 'bg-purple-100 text-purple-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${tone[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {REDO_STATUS_LABELS[status] ? t(REDO_STATUS_LABELS[status]) : status}
    </span>
  );
}

function RedoCard({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const { data: settingsData } = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get() });
  const { data: coversData } = useQuery({ queryKey: ['covers-all'], queryFn: () => coversApi.list() });
  const settings = settingsData?.settings;
  const covers = coversData?.covers ?? [];
  const rate = settings?.redo_cost_per_page ?? 4;

  const albumPdfs = order.album_pdfs ?? [];
  const albumOptions =
    albumPdfs.length > 0
      ? albumPdfs.map((a) => ({ index: a.album_index, label: a.destination }))
      : [{ index: 1, label: order.destination_1 ?? null }];

  const [albumIndex, setAlbumIndex] = useState<number>(albumOptions[0]?.index ?? 1);
  const [scope, setScope] = useState<RedoScope>('full');
  const [formOpen, setFormOpen] = useState(false);
  const [reason, setReason] = useState<RedoReason>('print-error');
  const [reasonDetail, setReasonDetail] = useState('');
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [redoBusyId, setRedoBusyId] = useState<string | null>(null);
  const isAdminRole = useAuthStore((st) => st.profile?.role === 'admin');

  // Album content pages — for the custom-pages picker AND the live cost preview
  // (full / interior-only cost = full content count). Loaded for every scope so
  // "Coût estimé" never reads a misleading "0 × rate = 0 MAD" before a page is
  // picked.
  const { data: sessData } = useQuery({
    queryKey: ['session-pages', shopifyOrderId, albumIndex],
    queryFn: () => sessionsApi.get(shopifyOrderId, albumIndex),
  });
  const sessionPages = (() => {
    const p = sessData?.session?.pages as { pages?: unknown[] } | null | undefined;
    return Array.isArray(p?.pages) ? p!.pages! : [];
  })();
  const pageCount = sessionPages.length;

  // Reset page picker when the album or scope changes.
  useEffect(() => { setSelectedPages([]); }, [albumIndex, scope]);

  // Live cost preview (internal tracking only).
  const costPages =
    scope === 'cover-only' ? 0
    : scope === 'custom-pages' ? selectedPages.length
    : pageCount; // full / interior-only → full content count (best-effort)
  const costMad = costPages * rate;

  const redos: OrderRedo[] = order.redos ?? [];

  async function handleCreate() {
    setError(null);
    if (scope === 'custom-pages' && selectedPages.length === 0) {
      setError(t('Sélectionnez au moins une page pour un redo "Pages spécifiques".'));
      return;
    }
    setBusy(true);
    try {
      setBusyMsg(t('Création du redo…'));
      const { redo } = await redosApi.create(shopifyOrderId, {
        album_index: albumIndex,
        scope,
        page_indices: scope === 'custom-pages' ? selectedPages : undefined,
        reason,
        reason_detail: reasonDetail.trim() || undefined,
      });

      // Only `custom-pages` needs a freshly-rendered SUBSET PDF. A full /
      // cover-only / interior-only redo is just a reprint of the album's
      // ALREADY-generated PDFs — reuse the stored session URLs instead of
      // re-rendering the whole album in the admin's browser (that round-trip
      // was slow + fragile, and silently stalled on big Trio albums → the
      // "rien ne se passe" report). Instant + reliable now.
      let coverUrl: string | null = null;
      let interiorUrl: string | null = null;

      if (scope === 'custom-pages') {
        setBusyMsg(t('Génération des pages (navigateur)…'));
        const urls = await renderAndUploadRedoPdfs({
          shopifyOrderId,
          orderId: order.id,
          redoId: redo.id,
          albumIndex,
          scope,
          pageIndices: selectedPages,
          covers,
          logoUrl: settings?.logo_url ?? null,
          introPageUrl: settings?.intro_page_1_url ?? null,
          outroPageUrl: settings?.outro_page_2_url ?? null,
          bleedMm: settings?.pdf_bleed_mm ?? 3,
          onProgress: (p) => setBusyMsg(p ? `${p.message}` : t('Génération des PDF…')),
        });
        coverUrl = urls.cover_pdf_url;
        interiorUrl = urls.interior_pdf_url;
      } else {
        setBusyMsg(t('Préparation des fichiers…'));
        const { session } = await sessionsApi.get(shopifyOrderId, albumIndex);
        if (scope === 'full' || scope === 'cover-only') coverUrl = session?.cover_pdf_url ?? null;
        if (scope === 'full' || scope === 'interior-only') interiorUrl = session?.interior_pdf_url ?? null;
        if (!coverUrl && !interiorUrl) {
          throw new Error(t("L'album n'a pas encore de PDF généré. Régénérez d'abord l'album, puis créez le redo."));
        }
      }

      setBusyMsg(t('Enregistrement…'));
      await redosApi.update(redo.id, {
        status: 'ready',
        cover_pdf_url: coverUrl ?? undefined,
        interior_pdf_url: interiorUrl ?? undefined,
      });

      setReasonDetail('');
      setSelectedPages([]);
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    } catch (e) {
      console.error('[redo] create failed:', e);
      setError((e as Error)?.message ?? t('Échec de la création du redo.'));
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    } finally {
      setBusy(false);
      setBusyMsg('');
    }
  }

  // "Envoyer à l'imprimeur" opens the SAME printer-link popup as the orders
  // page (Générer le lien → copyable /imprimeur/{token}) — the legacy
  // zip+MailChannels path (sendRedosToPrinter) is retired.
  const [printerLinkRedo, setPrinterLinkRedo] = useState<OrderRedo | null>(null);

  async function handleComplete(redo: OrderRedo) {
    setRedoBusyId(redo.id);
    setError(null);
    try {
      try {
        await redosApi.complete(redo.id);
      } catch (e) {
        // No print without the advance (2026-10-15): an admin may force it, with a reason.
        if (!isAdminRole || !isNotPaid(e)) throw e;
        const reason = askOverrideReason((e as Error).message);
        if (!reason) throw e;
        await redosApi.complete(redo.id, reason);
      }
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    } catch (e) {
      setError((e as Error)?.message ?? t('Échec de la finalisation.'));
    } finally {
      setRedoBusyId(null);
    }
  }

  return (
    <div id="sec-redo" className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-800">{t('Redo / Réimpression')}</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {t('Réimprimer tout ou partie de la commande. Le coût (papier + encre) est un suivi interne — jamais facturé au client.')}
          </p>
        </div>
        {/* Owner, 2026-10-11 — « I don't see it »: the card sits at the TOP of the
            order column; the form opens on demand so it does not push the rest down. */}
        {!(formOpen || busy) && (
          <Button size="sm" variant="secondary" className="shrink-0" onClick={() => setFormOpen(true)}>
            {t('Nouveau redo')}
          </Button>
        )}
      </div>

      {/* New redo form */}
      {(formOpen || busy) && (
      <div className="px-5 py-4 space-y-3 border-b border-gray-100">
        {albumOptions.length > 1 && (
          <label className="block">
            <span className="text-xs font-medium text-gray-600">{t('Album')}</span>
            <select
              value={albumIndex}
              onChange={(e) => setAlbumIndex(Number(e.target.value))}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
            >
              {albumOptions.map((a) => (
                <option key={a.index} value={a.index}>
                  {t('Album {n}', { n: a.index })}{a.label ? ` · ${a.label}` : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="block">
          <span className="text-xs font-medium text-gray-600">{t('Portée')}</span>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as RedoScope)}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
          >
            {(Object.keys(REDO_SCOPE_LABELS) as RedoScope[]).map((s) => (
              <option key={s} value={s}>{t(REDO_SCOPE_LABELS[s])}</option>
            ))}
          </select>
        </label>

        {scope === 'custom-pages' && (
          <div>
            <span className="text-xs font-medium text-gray-600">{t('Pages à réimprimer')}</span>
            {pageCount === 0 ? (
              <p className="text-xs text-gray-400 mt-1">{t('Chargement des pages…')}</p>
            ) : (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {Array.from({ length: pageCount }, (_, i) => {
                  const active = selectedPages.includes(i);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() =>
                        setSelectedPages((prev) =>
                          prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort((a, b) => a - b),
                        )
                      }
                      className={`w-9 h-9 rounded-lg text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-brand-500 text-white border-brand-500'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300'
                      }`}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <label className="block">
          <span className="text-xs font-medium text-gray-600">{t('Raison')}</span>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value as RedoReason)}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
          >
            {(Object.keys(REDO_REASON_LABELS) as RedoReason[]).map((r) => (
              <option key={r} value={r}>{t(REDO_REASON_LABELS[r])}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-gray-600">{t('Notes (optionnel)')}</span>
          <textarea
            value={reasonDetail}
            onChange={(e) => setReasonDetail(e.target.value)}
            rows={2}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            placeholder={t('Détails internes…')}
          />
        </label>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-gray-500">
            {t('Coût estimé :')} <b>{costPages}</b> × {rate} ={' '}
            <b>{costMad.toFixed(0)} MAD</b> <span className="text-gray-400">{t('— suivi interne')}</span>
          </p>
          <Button onClick={handleCreate} loading={busy} disabled={busy}>
            {t('Créer le redo')}
          </Button>
        </div>
        {busy && busyMsg && <p className="text-xs text-amber-600">{busyMsg}</p>}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
      )}

      {/* Redo history — only once there is one */}
      {redos.length > 0 && (
      <div className="px-5 py-4">
        <h3 className="text-xs font-semibold text-gray-600 mb-2">{t('Historique des redos')}</h3>
        {redos.length === 0 ? (
          <p className="text-xs text-gray-400">{t('Aucun redo pour cette commande.')}</p>
        ) : (
          <ul className="space-y-2">
            {redos.map((r) => (
              <li key={r.id} className="border border-gray-100 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-gray-700">
                      {t('Album {n}', { n: r.album_index })} · {t(REDO_SCOPE_LABELS[r.scope])}
                    </span>
                    <RedoStatusBadge status={r.status} />
                  </div>
                  <span className="text-[11px] text-gray-400">{formatDate(r.created_at)}</span>
                </div>
                <div className="text-[11px] text-gray-500">
                  {t('Raison :')} {t(REDO_REASON_LABELS[r.reason])}
                  {r.reason_detail ? ` — ${r.reason_detail}` : ''}
                  {r.scope === 'custom-pages' && r.page_indices?.length
                    ? ` · ${t('pages {list}', { list: r.page_indices.map((p) => p + 1).join(', ') })}`
                    : ''}
                  {' · '}
                  {t('Coût interne : {n} MAD', { n: (r.redo_cost_mad ?? 0).toFixed(0) })}
                </div>
                {/* "Envoyer à l'imprimeur" is available as soon as the redo has
                    a PDF (ready / sent-to-printer / completed) — same printer-
                    link popup as the orders page. "Marquer terminé" stays for
                    not-yet-completed redos only. */}
                {(r.cover_pdf_url || r.interior_pdf_url) && r.status !== 'cancelled' && (
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setPrinterLinkRedo(r)}
                      disabled={redoBusyId === r.id}
                    >
                      {t("Envoyer à l'imprimeur")}
                    </Button>
                    {r.status !== 'completed' && (
                      <Button
                        size="sm"
                        onClick={() => handleComplete(r)}
                        loading={redoBusyId === r.id}
                        disabled={redoBusyId === r.id}
                      >
                        {t('Marquer terminé')}
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      )}

      {printerLinkRedo && (
        <RedoPrinterLinkModal
          redo={printerLinkRedo}
          orderName={order.shopify_order_name}
          onClose={() => setPrinterLinkRedo(null)}
          onSent={() => queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] })}
        />
      )}
    </div>
  );
}

// ─── Redo printer-link modal ─────────────────────────────────────────────────
// Mirrors the orders-page SendToPrinterModal experience for ONE redo: instant
// "Générer le lien" → copyable app.storiz.ma/imprimeur/{token} (7 days, no
// login). The public page lists the redo's own subset PDFs. Generating the
// link also flips the redo to 'sent-to-printer'.
function RedoPrinterLinkModal({
  redo, orderName, onClose, onSent,
}: { redo: OrderRedo; orderName: string; onClose: () => void; onSent: () => void }) {
  const { t } = useT();
  const [submitting, setSubmitting] = useState(false);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileCount = (redo.cover_pdf_url ? 1 : 0) + (redo.interior_pdf_url ? 1 : 0);
  const scopeLabel = redo.scope === 'custom-pages' && redo.page_indices?.length
    ? t('pages {list}', { list: redo.page_indices.map((p) => p + 1).join(', ') })
    : redo.scope === 'cover-only' ? t('couverture uniquement')
    : redo.scope === 'interior-only' ? t('intérieur complet')
    : t('album complet');

  async function handleGenerate() {
    setSubmitting(true);
    setError(null);
    try {
      if (fileCount === 0) {
        setError(t('Ce redo n’a aucun PDF à envoyer. Générez d’abord les PDF.'));
        return;
      }
      const { token } = await ordersApi.createPrinterLink([], [redo.id]);
      setLinkUrl(`${window.location.origin}/imprimeur/${token}`);
      await redosApi.update(redo.id, { status: 'sent-to-printer' });
      onSent();
    } catch (e) {
      setError((e as Error)?.message ?? t('Échec de la création du lien.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function copyLink() {
    if (!linkUrl) return;
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — the field is selectable as a fallback */ }
  }

  return (
    <Modal isOpen={true} onClose={submitting ? () => {} : onClose} size="lg" title={t('Lien imprimeur (réimpression)')}>
      <div className="p-6 space-y-4">
        {!linkUrl ? (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
              {t('Redo')} <strong>{orderName}</strong> — {t('album {n}', { n: redo.album_index })}, {scopeLabel} · <strong>{fileCount}</strong> {fileCount > 1 ? t('fichiers PDF.') : t('fichier PDF.')}{' '}
              {t('Le lien (valable 7 jours, sans connexion) ouvre la même page de téléchargement que les commandes groupées.')}
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">{error}</div>}

            <div className="flex justify-end gap-2 pt-1 border-t border-gray-100">
              <Button variant="secondary" onClick={onClose} disabled={submitting}>{t('Annuler')}</Button>
              <Button variant="primary" loading={submitting} onClick={handleGenerate}>
                {t('Générer le lien')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              {t('Lien prêt — le redo est marqué')} <strong>{t("Envoyé à l'imprimeur")}</strong>.
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-600">{t("Lien à envoyer à l'imprimeur (WhatsApp / email) — valable 7 jours, sans connexion :")}</label>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={linkUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-xs font-mono text-gray-800 bg-gray-50"
                />
                <Button variant="primary" onClick={copyLink}>{copied ? t('Copié ✓') : t('Copier')}</Button>
              </div>
              <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-600 hover:underline">
                {t('Ouvrir la page de téléchargement →')}
              </a>
            </div>

            <div className="flex justify-end pt-1 border-t border-gray-100">
              <Button variant="secondary" onClick={onClose}>{t('Fermer')}</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const ALL_STATUSES = Object.entries(ORDER_STATUS_LABELS);

// ─── Redesign 2026-06-10 — pipeline layout primitives ────────────────────────
// The order page reads as the production flow it represents: sticky identity
// bar (status always reachable), pipeline rail with live step states (click =
// smooth-scroll), two-column body (workflow left, reference right).




function OrderCostsCard({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  const { data } = useQuery({
    queryKey: ['order-costs', shopifyOrderId],
    queryFn: () => printApi.orderCosts(shopifyOrderId),
    staleTime: 30_000,
  });
  const costs = data?.costs;
  const rows: Array<[string, number | null | undefined]> = [
    [t('Matières (stock)'), costs?.materials_cost],
    [t('Intérieur (imprimeur)'), costs?.interior_cost],
    [t('Couverture (imprimeur)'), costs?.cover_cost],
    [t('Livraison'), costs?.delivery_cost],
  ];
  const known = rows.filter(([, v]) => v != null) as Array<[string, number]>;
  const total = known.reduce((s, [, v]) => s + Number(v), 0);
  const revenue = order.total_price != null ? Math.round(Number(order.total_price)) : null;
  const margin = revenue != null && known.length > 0 ? revenue - total : null;
  const fmt = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString('fr-FR')} MAD`;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h2 className="text-sm font-semibold text-gray-800 mb-2.5">{t('💰 Coût de revient')}</h2>
      {known.length === 0 ? (
        <p className="text-xs text-gray-400">
          {t("Les coûts apparaissent au fil de la production : matières à l'« Imprimée », impression au lot imprimeur, livraison à la livraison.")}
        </p>
      ) : (
        <div className="space-y-1.5">
          {rows.map(([label, v]) => (
            <div key={label} className="flex items-baseline justify-between text-xs">
              <span className={v != null ? 'text-gray-600' : 'text-gray-300'}>{label}</span>
              <span className={`tabular-nums font-semibold ${v != null ? 'text-gray-800' : 'text-gray-300'}`}>
                {v != null ? fmt(Number(v)) : t('à venir')}
              </span>
            </div>
          ))}
          <div className="flex items-baseline justify-between text-xs pt-1.5 mt-1 border-t border-gray-100">
            <span className="font-bold text-gray-700">{t('Coût total')}</span>
            <span className="tabular-nums font-bold text-gray-900">{fmt(total)}</span>
          </div>
          {margin != null && (
            <div
              className="flex items-baseline justify-between text-sm rounded-lg px-2.5 py-2 mt-1"
              style={{ background: margin >= 0 ? 'rgba(16,185,129,.08)' : 'rgba(239,68,68,.07)' }}
            >
              <span className={`font-bold ${margin >= 0 ? 'text-[#047857] dark:text-status-done' : 'text-status-stuck'}`}>
                {t('Marge')} {revenue != null ? t('(sur {revenue})', { revenue: fmt(revenue) }) : ''}
              </span>
              <span className={`tabular-nums font-bold ${margin >= 0 ? 'text-[#047857] dark:text-status-done' : 'text-status-stuck'}`}>
                {fmt(margin)}
              </span>
            </div>
          )}
        </div>
      )}
      {/* AI costs (owner, 2026-10-22): the order's lifetime AI spend, apart from the cost of the book. */}
      <OrderAiCostLine shopifyOrderId={shopifyOrderId} />
    </div>
  );
}

/** Rentabilité — live profit breakdown from the stock cost model
 *  (CA − matières (recette × albums) − emballage − livraison − redo). */
function OrderProfitCard({ shopifyOrderId }: { shopifyOrderId: string }) {
  const { t } = useT();
  const { data, error } = useQuery({
    queryKey: ['order-profit', shopifyOrderId],
    queryFn: () => stockApi.orderProfit(shopifyOrderId),
    staleTime: 30_000,
    retry: false,
  });
  if (error) return null;
  return (
    <div className="rounded-xl border p-4" style={{ background: 'var(--color-bg-card)', borderColor: 'var(--color-border-subtle)' }}>
      <h2 className="text-sm font-semibold mb-2.5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: 'var(--color-accent-primary)' }}>{t('📈 Rentabilité')}</h2>
      {data?.order_profit
        ? <OrderProfitBody p={data.order_profit} />
        : <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{t('Calcul du bénéfice…')}</p>}
    </div>
  );
}

/** Quiet placeholder when a workflow step's card isn't applicable yet — keeps
 *  the pipeline numbering/anchors stable instead of sections vanishing. */
function StubCard({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="bg-white/60 rounded-xl border border-dashed p-5" style={{ borderColor: 'rgba(201,162,74,.3)' }}>
      <h2 className="text-sm font-semibold text-gray-400">{title}</h2>
      <p className="text-xs text-gray-400 mt-1">{hint}</p>
    </div>
  );
}

/** Danger zone stays collapsed until deliberately opened — destructive actions
 *  spatially separated, one click away (smooth grid-rows expand). */
function CollapsibleDanger({ children }: { children: React.ReactNode }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-red-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-[13px] font-semibold text-red-700 cursor-pointer"
      >
        <span aria-hidden>⚠</span> {t('Zone de danger')}
        <svg
          className={`w-4 h-4 ml-auto transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        >
          <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <div className="px-2 pb-2">{children}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Admin sticky notes (2026-07-15) ─────────────────────────────────────────
// Internal team notes pinned at the VERY TOP of the order page in a soft-red
// sticky-note style, so no admin can miss them. Each note shows WHO wrote it
// (admin account email) + when, and can be deleted. Adding is a small quiet
// button so the page stays clean when there's nothing to flag.

export default function AdminOrderDetailPage() {
  const { t } = useT();
  const { can } = usePermissions();
  // Assignment overrides are admin-only (2026-08-11) — same rule as the
  // cross-agent views: agents pull from the queue, they never hand out work.
  const isFullAdminUser = can('*');
  const canFinance = can('finance.view');  // gates the cost/profit cards
  const { shopifyOrderId } = useParams<{ shopifyOrderId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  // Publish the real height of (app header + this page's sticky command bar) as
  // --adm-sticky-h so anchor jumps clear it. The bar's height is not a constant:
  // it has 1–3 rows depending on width, whether the confirm stamp is present,
  // and whether the status-saved line is showing. Measured, therefore, not guessed.
  //
  // `stickyMeasureTick` is in the deps for a reason: this component returns a
  // loading skeleton before the order arrives, so on first mount the ref is still
  // null. With `[]` deps the effect would give up there and never re-run once the
  // real bar mounted, leaving the CSS fallback in place (verified live: the var
  // stayed at 180px while the bar measured 189px).
  const cmdBarRef = useRef<HTMLDivElement>(null);
  const [stickyMeasureTick, setStickyMeasureTick] = useState(0);
  useEffect(() => {
    const el = cmdBarRef.current;
    if (!el) {
      // Bar not mounted yet (still loading) — retry on the next frame.
      const raf = requestAnimationFrame(() => setStickyMeasureTick((n) => n + 1));
      return () => cancelAnimationFrame(raf);
    }
    const publish = () => {
      const appHeader = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--adm-header-h'),
      ) || 0;
      document.documentElement.style.setProperty(
        '--adm-sticky-h',
        `${Math.round(appHeader + el.getBoundingClientRect().height)}px`,
      );
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener('resize', publish);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', publish);
      document.documentElement.style.removeProperty('--adm-sticky-h');
    };
  }, [stickyMeasureTick]);

  const { data, isLoading } = useQuery({
    queryKey: ['order', shopifyOrderId],
    queryFn: () => ordersApi.get(shopifyOrderId!),
    enabled: !!shopifyOrderId,
    // Auto-poll while a PDF generation is in flight so the admin sees the
    // status flip from `generating` → `ready`/`failed` without a manual refresh.
    refetchInterval: (query) => {
      const status = (query.state.data as { order?: Order } | undefined)?.order?.pdf_status;
      if (status === 'generating') return 3000;
      // The 3D book is rebuilt after the PDF — keep watching until it lands so
      // « Régénérer » ends on the new preview without a manual refresh.
      return (query.state.data as { order?: { previews_status?: string | null } } | undefined)?.order?.previews_status === 'generating' ? 5000 : false;
    },
    // Refetch when the admin tab regains focus — covers the case where the
    // admin opens the editor in another tab, resubmits, and comes back. The
    // cached `cover_pdf_url` token would otherwise stay frozen at the value
    // captured on initial load even though the underlying PDF was overwritten.
    refetchOnWindowFocus: true,
    // staleTime 0 makes every remount (e.g. SPA route back from editor) refetch.
    staleTime: 0,
  });

  useEffect(() => {
    if (data?.order?.status) setSelectedStatus(data.order.status);
  }, [data?.order?.status]);

  // « Forcer sans avance » (owner, 2026-10-15): an admin may send a printing
  // status on with no advance received, with a written reason (logged).
  const isAdminRole = useAuthStore((st) => st.profile?.role === 'admin');
  const statusMutation = useMutation({
    mutationFn: (status: string) => changeStatusAsking(shopifyOrderId!, status, isAdminRole),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['order-pricing', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
    },
  });

  // "✓ Confirmer la commande" — the agent's one-click confirmation. Locks the
  // commission rate of the moment; a 409 (already confirmed elsewhere) just
  // refetches so the stamp appears.
  const confirmOrderMutation = useMutation({
    mutationFn: () => ordersApi.confirmOrder(shopifyOrderId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] }),
    onError: () => queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] }),
  });

  // Live pricing overlay. The order row's page_count / extras / total_price can
  // lag the album's ACTUAL content pages after a regen (regen skips /submit).
  // checkout-summary recomputes them from album_sessions.pages (and self-heals
  // the row), so overlaying its figures here flows the correct page count,
  // price, spine width AND AMEEX COD default through every child surface at
  // once — the children all read `order.*` already.
  const { data: pricingData } = useQuery({
    queryKey: ['order-pricing', shopifyOrderId],
    queryFn: () => ordersApi.checkoutSummary(shopifyOrderId!),
    enabled: !!shopifyOrderId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  // Pipeline rail state sources. Both keys are shared with SmartFollowUpCard /
  // PreviewLinkCard so react-query dedupes — zero extra network cost.
  const { data: fuData } = useQuery({
    queryKey: ['admin-order-follow-up', shopifyOrderId],
    queryFn: () => ordersApi.followUp(shopifyOrderId!),
    enabled: !!shopifyOrderId,
    staleTime: 30_000,
  });
  // Also the source of the top bar's « Aperçu 3D » shortcut: the same URL, and
  // the same "is there one", as the « Aperçu client » card in section 4.
  const { data: reviewData, isLoading: reviewLoading, isError: reviewError } = useQuery({
    queryKey: ['preview-review', shopifyOrderId],
    queryFn: () => previewLinkApi.adminNotes(shopifyOrderId!),
    enabled: !!shopifyOrderId,
    staleTime: 30_000,
  });

  // ── Order page redesign (2026-10-07): notes, open steps, the chat drawer, phone tabs ──
  const isPhone = useIsPhone();
  const notesState = useOrderNotes(shopifyOrderId ?? '');
  // ?step=preview|delivery — the home's « Voir l'aperçu » / « Créer le colis »
  // buttons open the order on that step (2026-10-21).
  const [searchParams] = useSearchParams();
  const wantStep = searchParams.get('step') as StepKey | null;
  const [openSteps, setOpenSteps] = useState<Set<StepKey> | null>(() => (wantStep ? new Set<StepKey>([wantStep]) : null));
  useEffect(() => {
    if (!wantStep) return;
    let tries = 0;
    const tm = window.setInterval(() => {
      const el = document.getElementById(`step-${wantStep}`);
      if (el || ++tries > 40) {
        window.clearInterval(tm);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
    return () => window.clearInterval(tm);
  }, [wantStep]);
  const [chatOpen, setChatOpen] = useState(false);
  const [phoneTab, setPhoneTab] = useState<'steps' | 'conversation' | 'order'>('steps');
  // The order's action bar replaces the app's bottom tabs on a phone (answer 5).
  useChatOpenSignal(isPhone);
  const { data: threadData } = useQuery({
    queryKey: ['order-thread', shopifyOrderId],
    queryFn: () => pilotageApi.orderThread(shopifyOrderId!),
    enabled: !!shopifyOrderId,
    refetchInterval: 60_000,
  });
  const { data: requestsData } = useQuery({
    queryKey: ['client-requests', shopifyOrderId],
    queryFn: () => clientRequestsApi.get(shopifyOrderId!),
    enabled: !!shopifyOrderId,
    staleTime: 15_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const requestsLeft = (requestsData?.items ?? []).filter((i) => !['done_owner', 'done_agent', 'done'].includes(i.status)).length;

  const rawOrder: Order | undefined = data?.order;
  const order: Order | undefined = rawOrder && pricingData
    ? {
        ...rawOrder,
        page_count: pricingData.page_count ?? rawOrder.page_count,
        extra_pages: pricingData.extra_pages ?? rawOrder.extra_pages,
        extra_pages_cost: pricingData.extra_pages_cost ?? rawOrder.extra_pages_cost,
        relief_3d: pricingData.relief_3d ?? rawOrder.relief_3d,
        relief_3d_cost: pricingData.relief_3d_cost ?? rawOrder.relief_3d_cost,
        total_price: pricingData.total ?? rawOrder.total_price,
      }
    : rawOrder;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-center">
        <div>
          <p className="text-gray-500 mb-4">{t('Commande introuvable')}</p>
          <Button onClick={() => navigate('/admin')}>{t('← Retour')}</Button>
        </div>
      </div>
    );
  }

  // ── Pipeline rail derivation (redesign 2026-06-10) ──────────────────────────
  const photoTotal = Object.values(fuData?.photo_counts ?? {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const hasAnyPdf =
    !!(order.cover_pdf_url || order.interior_pdf_url || order.full_book_pdf_url) ||
    (order.album_pdfs ?? []).some((r) => r.cover_pdf_url || r.interior_pdf_url || r.full_book_pdf_url);
  const isAdvanced = ['album-submitted', 'ready-to-print', 'in-production', 'printed', 'ready-to-deliver', 'shipped', 'delivered', 'in-redo'].includes(order.status);
  const steps: StepInfo[] = [
    { key: 'photos',   label: STEP_LABEL.photos,   done: photoTotal > 0 || isAdvanced },
    { key: 'album',    label: STEP_LABEL.album,    done: !!order.album_submitted_at || isAdvanced },
    { key: 'pdf',      label: STEP_LABEL.pdf,      done: hasAnyPdf || order.pdf_status === 'ready' },
    { key: 'preview',  label: STEP_LABEL.preview,  done: reviewData?.client_review_status === 'approved' },
    { key: 'delivery', label: STEP_LABEL.delivery, done: order.ameex_status === 'delivered' || order.status === 'delivered' },
    // « After delivery »: the satisfaction question, then the review link if she is happy (answer 4).
    { key: 'followup', label: STEP_LABEL.followup, done: false },
  ];
  const firstTodo = steps.findIndex((s) => !s.done);
  const currentIdx = firstTodo === -1 ? steps.length - 1 : firstTodo;
  const currentKey = steps[currentIdx].key;
  const opened = openSteps ?? new Set<StepKey>([currentKey]);
  const setStepOpen = (k: StepKey, open: boolean) => setOpenSteps(() => {
    const next = new Set(opened);
    if (open) next.add(k); else next.delete(k);
    return next;
  });
  const goStep = (k: StepKey) => {
    setPhoneTab('steps');
    setStepOpen(k, true);
    window.setTimeout(() => document.getElementById(`step-${k}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const chatId = threadData?.chatId ?? null;
  const openChat = () => { if (chatId) setChatOpen(true); };
  const showPdfCard = hasAnyPdf || isAdvanced;
  // What this order still needs, if anything (lib/orderActionState.ts). Uses the
  // review status already fetched for the preview card.
  const actionState = orderActionState({
    order: { ...order, client_review_status: reviewData?.client_review_status ?? null },
  });

  return (
    <AdminLayout>
      {/* ═══ Sticky command header: identity bar + pipeline rail (one unit) ═══ */}
      <div
        ref={cmdBarRef}
        // White glass in light (unchanged); the night chrome glass in dark —
        // the bar must not be a bright slab over a dark page.
        className="sticky z-30 md:top-0 -mt-5 md:-mt-7 -mx-4 md:-mx-7 px-4 md:px-7 border-b bg-[rgba(255,255,255,0.93)] dark:bg-adm-chrome"
        style={{
          // Offset by the app's own mobile header (--adm-header-h), never a
          // hardcoded px value — the old top-[57px] was 8px short of the real
          // 65px header and the two bars overlapped.
          top: 'var(--adm-header-h)',
          backdropFilter: 'blur(10px)',
          borderColor: 'rgba(201,162,74,0.2)',
        }}
      >
        {/* Identity bar — MOBILE-FIRST rows, desktop keeps the single line.
            On a phone the old version wrapped three shrink-0 groups into ~150px
            of sticky height and hid bundle + price behind `hidden sm:inline` —
            the two facts an agent most needs. Now: identity + confirm on row 1,
            the order facts on row 2, status controls on row 3. */}
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center gap-2 md:gap-4 py-2.5 md:flex-wrap">
          {/* Row 1 — back, identity, confirm. Confirm rides here on mobile too:
              it is the Confirmation Agent's single most-used control. */}
          <div className="flex items-center gap-3 min-w-0 md:contents">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label={t('Retour')}
              className="w-11 h-11 md:w-9 md:h-9 shrink-0 rounded-xl border flex items-center justify-center text-gray-500 hover:text-gray-800 hover:-translate-x-px transition-all bg-white"
              style={{ borderColor: 'rgba(201,162,74,0.25)' }}
            >
              ‹
            </button>
            {/* md:min-w — from md up row 1 dissolves into the bar's single
                wrapping line, where this grows from 0 and splits the free space
                with the spacer: it was 58 px wide at 1024 px (name cut, facts
                five lines tall). With a floor the line wraps the status
                controls instead of crushing the order (2026-10-01). */}
            <div className="min-w-0 flex-1 md:min-w-[150px]">
              <div
                className="text-[15px] md:text-[17px] font-semibold truncate leading-tight"
                style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: '0.01em' }}
              >
                {/* Action indicator — same rules as the orders list. Quiet dot,
                    only when this order needs something. */}
                {actionState && (
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
                    style={{ background: actionState.color }}
                    title={actionState.label}
                    aria-label={actionState.label}
                    role="img"
                  />
                )}
                <span className="text-adm-gold-ink">{order.shopify_order_name}</span>
                {order.customer_name && <span className="text-gray-800">&ensp;{order.customer_name}</span>}
              </div>
              {/* Order facts. Phone is tap-to-call on a phone; bundle and price
                  are now visible at every width (they were sm:-hidden). */}
              <div className="text-[11.5px] text-gray-500 flex items-center gap-x-2 gap-y-0.5 flex-wrap leading-tight mt-0.5">
                {order.customer_phone && (
                  <a href={`tel:${order.customer_phone}`} className="hover:text-gray-700" onClick={(e) => e.stopPropagation()}>
                    📞 {order.customer_phone}
                  </a>
                )}
                <span>
                  {order.bundle ?? 'Solo'} · {t('{n} pages', { n: order.page_count })}
                </span>
                {order.total_price != null && (
                  <span className="font-medium text-gray-600">
                    {Math.round(Number(order.total_price))} MAD · {order.payment_method === 'virement' ? t('Virement') : 'COD'}
                  </span>
                )}
              </div>
            </div>
            {/* « Scanner » + « Aperçu 3D » (parcel scanner, 2026-10-01). In
                ROW 1, never a row of their own: on a phone this bar already
                takes 190–270 px. Each hides without its permission. */}
            <div className="hidden lg:block shrink-0"><MoneyBlock order={order} /></div>
            {!isPhone && chatId && (
              <button type="button" onClick={openChat}
                className="shrink-0 inline-flex items-center gap-1.5 h-11 px-3 rounded-xl bg-adm-gold text-[#2B2116] dark:text-[#2B2116] text-[12.5px] font-semibold cursor-pointer hover:brightness-95">
                💬 {t('Ouvrir le chat')}
              </button>
            )}
            {!isPhone && chatId && <PaymentRequestButton target={{ order: order.shopify_order_id }} />}
            {!isPhone && (
              <OrderBarShortcuts
                preview={{ url: reviewData?.preview_url, loading: reviewLoading, error: reviewError, pdfsExist: hasAnyPdf }}
              />
            )}
          </div>
          {/* (No spacer here any more, 2026-10-01: `hidden md:block md:flex-1`
              grew as much as the identity and took HALF the spare room from
              md up — 186 px of nothing beside a 186 px identity at 1280 px.
              The identity is flex-1 and pushes what follows to the right.) */}
          {/* Admin override for the claim queue (2026-08-11). Admin-only: an
              agent must not be able to hand work to themselves or take it from
              a colleague — they pull from the shared queue instead. */}

          {/* Agent confirmation stamp / action (commission system, 2026-07-21).
              One click per order; the commission rate is locked at click time. */}
          <div className="shrink-0 hidden md:block">
            {order.agent_confirmation ? (() => {
              // The API strips the confirming agent's identity and locked rate
              // unless you are a full admin or the agent who confirmed it
              // (2026-08-11, worker lib/agentScope.ts). When that happens we
              // still know the order IS confirmed and when — so show that, and
              // no empty "par —" or a NaN rate tooltip.
              const conf = order.agent_confirmation;
              const who = conf.profiles?.full_name ?? conf.profiles?.email ?? null;
              const rate = Number(conf.rate);
              const date = new Date(conf.confirmed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
              // Confirmed by the worker after a person recorded the payment
              // (2026-10-02): say so, rather than « par » the profile it is
              // filed under — nobody clicked.
              const auto = conf.source === 'auto_payment';
              return (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-[#0f7a4f] dark:text-status-done"
                  style={{ background: 'rgba(16,185,129,.10)', border: '1px solid rgba(16,185,129,.30)' }}
                  title={Number.isFinite(rate) ? `Taux verrouillé : ${Math.round(rate)} DH` : undefined}
                >
                  ✓ {auto
                    ? t('Confirmée automatiquement (paiement) le {date}', { date })
                    : who
                    ? t('Confirmée par {name} le {date}', { name: who, date })
                    : t('Confirmée le {date}', { date })}
                </span>
              );
            })() : (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => confirmOrderMutation.mutate()}
                loading={confirmOrderMutation.isPending}
              >
                ✓ {t('Confirmer la commande')}
              </Button>
            )}
          </div>
          {/* Status controls. min-w-0 + flex-1 on the select so it compresses
              instead of pushing the row wider than the viewport. */}
          <div className="hidden md:flex items-center gap-2 md:shrink-0 min-w-0">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="flex-1 min-w-0 md:flex-none px-3 py-2 border rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white md:max-w-[180px]"
              style={{ borderColor: 'rgba(201,162,74,0.3)' }}
            >
              {ALL_STATUSES.map(([value, label]) => (
                <option key={value} value={value}>{t(label)}</option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={() => statusMutation.mutate(selectedStatus)}
              loading={statusMutation.isPending}
              disabled={selectedStatus === order.status}
            >
              {t('Mettre à jour')}
            </Button>
          </div>
          {statusMutation.isSuccess && (
            <p className="w-full text-[11px] text-green-600 -mt-1 pb-1 md:pb-0 md:-mb-1">
              {t('✓ Statut synchronisé avec Shopify')}
            </p>
          )}
          {/* ⚠️ A REFUSED STATUS CHANGE SAYS WHY (2026-09-30). The worker answers
              409 with the reason — photos without a printable copy, or a
              WhatsApp draft missing its name / city / address — and this page
              used to drop it, so « Mettre à jour » looked dead (#WA-561228). */}
          {statusMutation.isError && (
            <p role="alert" className="w-full text-[11px] text-red-600 -mt-1 pb-1 md:pb-0 md:-mb-1">
              ⚠️ {t('Statut non modifié :')} {(statusMutation.error as Error)?.message}
            </p>
          )}
        </div>

        {/* Pipeline rail — and, on the same line, the way to the client's
            open requests (only while there are some; no new row). */}
        <div className="max-w-6xl mx-auto pt-1 pb-2">
          <StepBar steps={steps} current={currentKey} open={opened} onOpen={goStep} requestsLeft={requestsLeft} />
        </div>
        {isPhone && (
          // Phone: Steps · Conversation · Order (sketch frame 4-5).
          <div role="tablist" className="max-w-6xl mx-auto flex gap-1 pb-2">
            {([['steps', 'Étapes'], ['conversation', 'Conversation'], ['order', 'Commande']] as const).map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={phoneTab === k} onClick={() => setPhoneTab(k)}
                className={`flex-1 h-9 rounded-lg text-[13px] cursor-pointer ${phoneTab === k ? 'bg-text-primary text-bg-card font-semibold' : 'bg-bg-elevated text-text-secondary'}`}>
                {t(label)}
              </button>
            ))}
          </div>
        )}
      </div>

      {(() => {
        // ── Each step's content: the very cards the page always had ──────────
        const stepNodes: Record<StepKey, React.ReactNode> = {
          photos: (
            <div className="space-y-3">
              <ClientPhotosCard order={order} />
              <UploadLinkCard order={order} shopifyOrderId={shopifyOrderId!} />
            </div>
          ),
          album: (
            <div className="space-y-3">
              {/* A11: the client's cover choice link lives with the album. */}
              <CoverPickLinkRow shopifyOrderId={shopifyOrderId ?? ''} />
              <AdminEditAlbumCard order={order} shopifyOrderId={shopifyOrderId!} />
              <AddAlbumCard order={order} shopifyOrderId={shopifyOrderId!} />
            </div>
          ),
          pdf: showPdfCard
            ? <PdfGenerationCard order={order} shopifyOrderId={shopifyOrderId!} />
            : <StubCard title={t('Production PDF')} hint={t("Disponible dès que l'album est soumis — ou ouvrez l'éditeur et cliquez Commander pour générer les PDF vous-même.")} />,
          preview: (
            <div className="space-y-3">
              {showPdfCard
                ? <PreviewLinkCard order={order} shopifyOrderId={shopifyOrderId!} />
                : <StubCard title={t('Aperçu client — flipbook 3D')} hint={t("Les aperçus sont générés à partir des PDF d'impression. Générez d'abord les PDF (étape 3).")} />}
              <div id="sec-requests"><ClientRequestsCard shopifyOrderId={shopifyOrderId!} previewUrl={reviewData?.preview_url ?? null} /></div>
            </div>
          ),
          delivery: (
            <div className="space-y-3">
              <AmeexDeliveryCard order={order} shopifyOrderId={shopifyOrderId!} />
              <AgentJourneyCard orderId={order.id} />
            </div>
          ),
          followup: order.customer_phone
            ? <SmartFollowUpCard shopifyOrderId={shopifyOrderId!} />
            : <StubCard title={t('Relance WhatsApp')} hint={t('Aucun numéro de téléphone sur cette commande.')} />,
        };
        const { notes, add, del, done } = notesState;
        const stepsColumn = (
          <div className="space-y-3 min-w-0">
            <PinnedProblems notes={notes} onGoStep={goStep}
              onDone={(id, d) => done.mutate({ id, done: d })} onDelete={(id) => del.mutate(id)} />
            {stepOrder(steps, currentKey).map((s) => (
              <StepFold key={s.key} step={s} index={steps.indexOf(s) + 1} current={s.key === currentKey}
                open={opened.has(s.key)} onToggle={(o) => { if (o !== opened.has(s.key)) setStepOpen(s.key, o); }}
                notes={notes.filter((n) => n.step === s.key)} onDeleteNote={(id) => del.mutate(id)}>
                {stepNodes[s.key]}
              </StepFold>
            ))}
            <ActivityCard notes={notes} add={add} onDelete={(id) => del.mutate(id)} onDone={(id, d) => done.mutate({ id, done: d })} />
          </div>
        );
        const conversationPanel = (
          <>
            <OrderConversationCard orderKey={shopifyOrderId!} onOpenChat={chatId ? openChat : undefined} />
            <OrderUpsellsCard order={order} />
            <OrderAddonsCard order={order} shopifyOrderId={shopifyOrderId!} />
          </>
        );
        const statusControls = (
          <div className="rounded-xl border border-border-subtle bg-bg-card p-4 space-y-3">
            <div className="flex items-center gap-2">
              <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} aria-label={t('Statut')}
                className="flex-1 min-w-0 h-11 px-3 border rounded-xl text-[13px] font-semibold bg-white" style={{ borderColor: 'rgba(201,162,74,0.3)' }}>
                {ALL_STATUSES.map(([value, label]) => <option key={value} value={value}>{t(label)}</option>)}
              </select>
              <Button size="sm" onClick={() => statusMutation.mutate(selectedStatus)} loading={statusMutation.isPending} disabled={selectedStatus === order.status}>
                {t('Mettre à jour')}
              </Button>
            </div>
            {statusMutation.isSuccess && <p className="text-[11px] text-green-600">{t('✓ Statut synchronisé avec Shopify')}</p>}
            {statusMutation.isError && <p role="alert" className="text-[11px] text-red-600">⚠️ {t('Statut non modifié :')} {(statusMutation.error as Error)?.message}</p>}
            <MoneyBlock order={order} />
          </div>
        );
        const orderPanel = (
          <>
            {order.album_submitted_at && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                <p className="text-xs text-green-700 font-medium">{t('✓ Album soumis le {date}', { date: formatDate(order.album_submitted_at) })}</p>
              </div>
            )}
            <OrderInfoCard order={order} shopifyOrderId={shopifyOrderId!} />
            {/* Coupon (2026-10-28): nothing unless codes are accepted or the order has one. */}
            <OrderCouponCard order={order} shopifyOrderId={shopifyOrderId!} />
            {/* Photo Vivante (2026-11-03): open when the order carries the option;
                folded « Ajouter une Photo Vivante » otherwise (2026-11-04). */}
            <LivingPhotoCard shopifyOrderId={shopifyOrderId!} albums={albumCountForBundle(order.bundle as 'Solo' | 'Duo' | 'Trio' | null)} />
            {showPdfCard && <RedoCard order={order} shopifyOrderId={shopifyOrderId!} />}
            {/* A10: who handles it — admins only. */}
            {isFullAdminUser && (
              <div className="rounded-xl border border-border-subtle bg-bg-card p-4">
                <p className="text-[12px] font-semibold text-text-secondary mb-2">{t('Traitée par')}</p>
                <OrderAssignmentControl shopifyOrderId={shopifyOrderId ?? ''} assignedAgentId={(order as { assigned_agent_id?: string | null }).assigned_agent_id} />
              </div>
            )}
            {canFinance && (<><OrderCostsCard order={order} shopifyOrderId={shopifyOrderId!} /><OrderProfitCard shopifyOrderId={shopifyOrderId!} /></>)}
            {/* Danger zone, folded: delete the order, ban the client (answer 9). */}
            <CollapsibleDanger>
              <div className="space-y-3">
                <BanClientControl order={order} />
                <DangerZoneCard order={order} shopifyOrderId={shopifyOrderId!} />
              </div>
            </CollapsibleDanger>
          </>
        );

        if (isPhone) {
          return (
            <div className="mt-3 space-y-3" style={{ paddingBottom: 'calc(76px + env(safe-area-inset-bottom, 0px))' }}>
              {phoneTab === 'steps' && stepsColumn}
              {phoneTab === 'conversation' && <div className="space-y-3">{conversationPanel}</div>}
              {phoneTab === 'order' && <div className="space-y-3">{statusControls}{orderPanel}</div>}
              {/* The order's own bar replaces the app's bottom tabs (answer 5). */}
              <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border-subtle bg-bg-card/95 backdrop-blur px-3 pt-2 flex items-center gap-2"
                style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' }}>
                <button type="button" onClick={openChat} disabled={!chatId}
                  className="flex-1 h-11 rounded-xl bg-adm-gold text-[#2B2116] dark:text-[#2B2116] text-[13px] font-semibold cursor-pointer disabled:opacity-40">💬 {t('Chat')}</button>
                {order.agent_confirmation
                  ? <span className="flex-1 h-11 inline-flex items-center justify-center rounded-xl text-[12px] font-semibold text-[#0f7a4f] dark:text-status-done bg-[rgba(16,185,129,.10)]">✓ {t('Confirmée')}</span>
                  : <button type="button" onClick={() => confirmOrderMutation.mutate()} disabled={confirmOrderMutation.isPending}
                      className="flex-1 h-11 rounded-xl border border-border-default bg-bg-card text-[13px] font-semibold text-text-primary cursor-pointer disabled:opacity-40">✓ {t('Confirmer')}</button>}
                <OrderBarShortcuts preview={{ url: reviewData?.preview_url, loading: reviewLoading, error: reviewError, pdfsExist: hasAnyPdf }} />
              </div>
              {chatOpen && chatId && <ChatDrawer chatId={chatId} name={order.customer_name ?? null} phone={order.customer_phone ?? null} onClose={() => setChatOpen(false)} />}
            </div>
          );
        }

        return (
          <div className="max-w-6xl mx-auto mt-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4 items-start">
            {stepsColumn}
            {/* The panel scrolls on its own (« long lists get their own frame »). */}
            <div className="flex flex-col gap-4 lg:sticky lg:top-[calc(var(--adm-sticky-h,160px)+12px)] lg:max-h-[calc(100vh-var(--adm-sticky-h,160px)-24px)] lg:overflow-y-auto lg:pr-0.5" style={{ scrollbarWidth: 'thin' }}>
              {conversationPanel}
              {orderPanel}
            </div>
            {chatOpen && chatId && <ChatDrawer chatId={chatId} name={order.customer_name ?? null} phone={order.customer_phone ?? null} onClose={() => setChatOpen(false)} />}
          </div>
        );
      })()}
    </AdminLayout>
  );
}

// Small gold check badge for the selected cover thumbnail.
function CoverCheck() {
  return (
    <span
      className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center shadow"
      style={{ background: 'var(--color-accent-primary)' }}
    >
      <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

// ─── Add Album Card ─────────────────────────────────────────────────────────
// Lets the admin ADD a new album to an existing order: set its price, cover and
// details (destination / year / page count), then generate a client share link
// so the customer continues uploading + building in the NORMAL flow. The album
// price is added to the order's total_price; the bundle bumps to match the new
// album count (Solo→Duo→Trio).
function AddAlbumCard({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const { data: coversData } = useQuery({ queryKey: ['covers-all'], queryFn: () => coversApi.list() });
  const covers = coversData?.covers ?? [];

  const currentCount =
    order.bundle === 'Trio' ? 3 : order.bundle === 'Duo' ? 2 : 1;
  const nextIndex = currentCount + 1;
  // The server reprices by tier on add, so the resulting bundle is determined by
  // the new album count (Solo→Duo→Trio); the album_price field is ignored.
  const nextBundle = nextIndex >= 3 ? 'Trio' : nextIndex === 2 ? 'Duo' : 'Solo';

  // Pre-fill the new album's price from the order's package so the admin
  // doesn't retype (Solo 399 / Duo 749 / Trio 1100). Pages pre-fill from the
  // order's current page count. Both stay editable.
  const BASE_PRICE_BY_BUNDLE: Record<string, number> = { Solo: 399, Duo: 749, Trio: 1100 };
  const defaultPrice = String(BASE_PRICE_BY_BUNDLE[order.bundle ?? 'Solo'] ?? 399);
  const defaultPages = String(order.page_count ?? 30);

  // The total the SERVER will land on for a given album count. Mirrors
  // `tierBasePriceForCount` in worker/src/lib/tierPricing.ts: 1/2/3 use the
  // named bundles, 4+ is ABOVE_TRIO_PER_ALBUM (366) x count.
  //
  // ⚠️ Do NOT quote BASE_PRICE_BY_BUNDLE[nextBundle] here. `nextBundle`
  // collapses every count >= 3 to 'Trio', so a 4th album would be shown as
  // 1100 MAD while the worker actually charges 366 x 4 = 1464 — the admin
  // would read a figure 364 MAD below what the customer is billed.
  const ABOVE_TRIO_PER_ALBUM = 366;
  const basePriceForAlbumCount = (n: number): number => {
    const count = Math.floor(Number(n) || 0);
    if (count <= 1) return BASE_PRICE_BY_BUNDLE.Solo;
    if (count === 2) return BASE_PRICE_BY_BUNDLE.Duo;
    if (count === 3) return BASE_PRICE_BY_BUNDLE.Trio;
    return ABOVE_TRIO_PER_ALBUM * count;
  };

  const [open, setOpen] = useState(false);
  const [destination, setDestination] = useState('');
  const [year, setYear] = useState('');
  const [pageCount, setPageCount] = useState<string>(defaultPages);
  const [albumPrice, setAlbumPrice] = useState<string>(defaultPrice);
  const [coverId, setCoverId] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [coverQuery, setCoverQuery] = useState('');

  function reset() {
    setDestination(''); setYear('');
    setPageCount(defaultPages);
    setAlbumPrice(defaultPrice); setCoverId(null);
    setError(null); setShareUrl(null); setCopied(false);
    setCoverQuery('');
  }

  // Accent-insensitive cover search over name + categories + search_tags.
  const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
  const filteredCovers = (() => {
    const q = norm(coverQuery);
    if (!q) return covers;
    return covers.filter((cv: Cover) => {
      const tags = Array.isArray((cv as { search_tags?: unknown }).search_tags)
        ? ((cv as { search_tags?: unknown[] }).search_tags as unknown[]).map((t) => String(t))
        : [];
      const cats = Array.isArray((cv as { categories?: unknown }).categories)
        ? ((cv as { categories?: unknown[] }).categories as unknown[]).map((t) => String(t))
        : [];
      return norm([cv.name ?? '', ...cats, ...tags].join(' ')).includes(q);
    });
  })();

  function closeModal() {
    setOpen(false);
    // If an album was created (shareUrl present) refresh so it shows everywhere.
    if (shareUrl) queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    reset();
  }

  function waUrl(): string | null {
    if (!shareUrl || !order.customer_phone) return null;
    const digits = order.customer_phone.replace(/\D/g, '');
    const intl = digits.startsWith('212') ? digits
      : digits.startsWith('0') ? '212' + digits.slice(1)
      : digits;
    const firstName = (order.customer_name ?? '').split(' ')[0] || '';
    const dest = destination.trim();
    const text = `Bonjour ${firstName} 👋

Nous avons ajouté un nouvel album${dest ? ` (${dest})` : ''} à votre commande Storiz. Continuez ici en ajoutant vos photos :
${shareUrl}

À très vite ! 📖✨`;
    return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
  }

  async function handleSubmit() {
    setError(null);
    const price = Number(albumPrice);
    if (!Number.isFinite(price) || price < 0) {
      setError(t('Entrez un prix valide (≥ 0) pour le nouvel album.'));
      return;
    }
    setBusy(true);
    try {
      // 1. Create the album on the order.
      await ordersApi.addAlbum(shopifyOrderId, {
        destination: destination.trim() || undefined,
        year: year.trim() || undefined,
        cover_id: coverId,
        page_count: pageCount ? Math.max(1, parseInt(pageCount, 10) || 0) : undefined,
        album_price: price,
      });
      // 2. Generate the client share link (resume into the normal flow).
      const { continue_url } = await resumeLinkApi.generate(shopifyOrderId);
      setShareUrl(continue_url);
      // Refresh the order query so the new album shows immediately.
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['order-pricing', shopifyOrderId] });
    } catch (e) {
      setError((e as Error)?.message ?? t("Échec de l'ajout de l'album."));
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  }

  // Self-contained modal: lock body scroll + close on Escape while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const labelCls = 'block text-xs font-semibold mb-1';
  const inputCls = 'w-full px-3.5 py-2.5 rounded-lg text-sm border outline-none transition-shadow focus:ring-2';
  // Night: inputs need a ≥ 3:1 edge (adm-line-strong). The light edge stays the
  // soft border-subtle; --adm-input-line is only ever set under html.adm-dark.
  const inputStyle = { borderColor: 'var(--adm-input-line, var(--color-border-subtle))', background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' } as const;

  return (
    <div
      className="rounded-xl border p-5"
      style={{ background: 'var(--color-bg-card)', borderColor: 'var(--color-border-subtle)' }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2
            className="text-sm font-semibold"
            style={{ fontFamily: "'Fraunces', Georgia, serif", color: 'var(--color-text-primary)' }}
          >
            {t('➕ Ajouter un album')}
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-tertiary)' }}>
            {t("Crée un nouvel album sur cette commande (deviendra l'album {n}) et génère un lien client pour continuer l'envoi des photos.", { n: nextIndex })}
          </p>
        </div>
        <Button onClick={() => { reset(); setOpen(true); }}>{t('Ajouter un album')}</Button>
      </div>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-6 dark:[--adm-input-line:var(--color-adm-line-strong)]"
          role="dialog"
          aria-modal="true"
          aria-label={t('Créer un album')}
          onClick={closeModal}
        >
          {/* Dimmed + blurred backdrop — covers the whole page (rendered in a
              body portal so no transformed ancestor can trap/clip it). */}
          <div className="absolute inset-0" style={{ background: 'rgba(46,36,24,0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }} />

          {/* Contained card: capped height, internal scroll, never bleeds */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-2xl"
            style={{ background: 'var(--color-bg-card)', border: `1px solid var(--color-border-subtle)` }}
          >
            {/* Header */}
            <div
              className="shrink-0 flex items-start justify-between gap-3 px-5 sm:px-6 py-4 border-b"
              style={{ borderColor: 'var(--color-border-subtle)', background: 'var(--color-bg-elevated)' }}
            >
              <div className="min-w-0">
                <h3 className="text-lg font-semibold leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", color: 'var(--color-text-primary)' }}>
                  {t('Créer un album')}
                </h3>
                <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('Commande')} <span className="font-semibold">{order.shopify_order_name ?? `#${shopifyOrderId}`}</span>
                  {order.customer_name ? <> · {order.customer_name}</> : null} · {t("deviendra l'album {n}", { n: nextIndex })}
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                aria-label={t('Fermer')}
                className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors hover:bg-black/5 dark:hover:bg-adm-surface-3"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
              </button>
            </div>

            {/* Body — the only outer scroller */}
            <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5">
            {!shareUrl ? (
              <div className="space-y-5">
                {/* Détails — clean 2-column grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                  <label className="block">
                    <span className={labelCls} style={{ color: 'var(--color-text-secondary)' }}>{t('Destination')}</span>
                    <input type="text" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder={t('ex. Marrakech')} className={inputCls} style={inputStyle} />
                  </label>
                  <label className="block">
                    <span className={labelCls} style={{ color: 'var(--color-text-secondary)' }}>{t('Année')}</span>
                    <input type="text" value={year} onChange={(e) => setYear(e.target.value)} placeholder={t('ex. 2026')} className={inputCls} style={inputStyle} />
                  </label>
                  <label className="block">
                    <span className={labelCls} style={{ color: 'var(--color-text-secondary)' }}>{t('Nombre de pages')}</span>
                    <input type="number" min={1} value={pageCount} onChange={(e) => setPageCount(e.target.value)} className={inputCls} style={inputStyle} />
                  </label>
                  <label className="block">
                    <span className={labelCls} style={{ color: 'var(--color-text-secondary)' }}>{t("Prix de l'album (MAD)")}</span>
                    <input type="number" min={0} value={albumPrice} onChange={(e) => setAlbumPrice(e.target.value)} placeholder={t('ex. 399')} className={inputCls} style={inputStyle} />
                    <span className="block mt-1 text-[11px]" style={{ color: 'var(--color-text-tertiary)' }}>
                      {t('Ignoré — le total est calculé par formule. Nouveau total : {b} ({p} MAD).', {
                        b: nextBundle,
                        p: basePriceForAlbumCount(nextIndex),
                      })}
                    </span>
                  </label>
                </div>

                {/* Couverture — fixed-height contained scroll grid */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>{t('Couverture')}</span>
                    <span className="text-[11px]" style={{ color: 'var(--color-text-tertiary)' }}>
                      {coverId ? t('Couverture sélectionnée') : t('Vierge / sans couverture')}
                    </span>
                  </div>
                  {/* Cover search */}
                  <div className="relative mb-2">
                    <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--color-text-tertiary)' }}>
                      <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" strokeLinecap="round" />
                    </svg>
                    <input
                      type="text"
                      value={coverQuery}
                      onChange={(e) => setCoverQuery(e.target.value)}
                      placeholder={t('Rechercher une couverture…')}
                      className="w-full pl-9 pr-9 py-2.5 rounded-lg text-sm border outline-none focus:ring-2"
                      style={inputStyle}
                    />
                    {coverQuery && (
                      <button type="button" onClick={() => setCoverQuery('')} aria-label={t('Effacer')} className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center hover:bg-black/5 dark:hover:bg-adm-surface-3" style={{ color: 'var(--color-text-secondary)' }}>
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
                      </button>
                    )}
                  </div>
                  <div className="rounded-xl border p-2" style={{ borderColor: 'var(--color-border-subtle)', background: 'var(--color-bg-primary)' }}>
                    <div className="grid grid-cols-3 sm:grid-cols-4 auto-rows-min content-start gap-2 h-64 sm:h-72 overflow-y-auto pr-1">
                      {/* Sans couverture — hidden while searching */}
                      {!coverQuery.trim() && (
                      <button
                        type="button"
                        onClick={() => setCoverId(null)}
                        className="relative aspect-[3/4] rounded-lg border-2 flex items-center justify-center text-[11px] text-center px-1 transition-all"
                        style={{
                          borderColor: coverId === null ? 'var(--color-accent-primary)' : 'var(--color-border-subtle)',
                          background: 'var(--color-bg-card)',
                          color: 'var(--color-text-primary)',
                          boxShadow: coverId === null ? `0 0 0 3px var(--color-accent-soft)` : 'none',
                        }}
                      >
                        {t('Vierge')}
                        {coverId === null && <CoverCheck />}
                      </button>
                      )}
                      {filteredCovers.map((cv: Cover) => (
                        <button
                          key={cv.id}
                          type="button"
                          onClick={() => setCoverId(cv.id)}
                          className="relative aspect-[3/4] rounded-lg border-2 overflow-hidden transition-all"
                          // a cover on its own colour plate — a light island at night
                          data-adm-media
                          style={{
                            borderColor: coverId === cv.id ? 'var(--color-accent-primary)' : 'var(--color-border-subtle)',
                            background: cv.dominant_color || 'var(--color-bg-card)',
                            boxShadow: coverId === cv.id ? `0 0 0 3px var(--color-accent-soft)` : 'none',
                          }}
                          title={cv.name}
                        >
                          {cv.thumbnail_url ? (
                            <img src={cv.thumbnail_url} alt={cv.name} className="w-full h-full object-contain" loading="lazy" />
                          ) : (
                            <span className="text-[10px] px-1 flex items-center justify-center h-full text-center" style={{ color: 'var(--color-text-secondary)' }}>{cv.name}</span>
                          )}
                          {coverId === cv.id && <CoverCheck />}
                        </button>
                      ))}
                      {coverQuery.trim() && filteredCovers.length === 0 && (
                        <p className="col-span-full text-center text-xs py-6" style={{ color: 'var(--color-text-tertiary)' }}>
                          {t('Aucune couverture pour « {q} »', { q: coverQuery.trim() })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {error && (
                  <p className="text-xs px-3 py-2 rounded-lg" style={{ color: 'var(--color-error)', background: 'rgba(184,92,61,0.08)' }}>{error}</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl border px-4 py-3" style={{ borderColor: 'var(--color-border-subtle)', background: 'var(--color-bg-primary)' }}>
                  <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {t('✓ Album {n} ajouté', { n: nextIndex })}{destination.trim() ? ` · ${destination.trim()}` : ''}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    {t('Nouveau total (formule {b}) :', { b: nextBundle })}&nbsp;<b>{formatPrice(basePriceForAlbumCount(nextIndex))}</b>
                  </p>
                </div>
                <label className="block">
                  <span className={labelCls} style={{ color: 'var(--color-text-secondary)' }}>{t('Lien client — à partager (continue dans le flux normal)')}</span>
                  <div className="mt-1 flex gap-2">
                    <input readOnly value={shareUrl} onFocus={(e) => e.currentTarget.select()} className="flex-1 px-3 py-2.5 border rounded-lg text-xs font-mono" style={inputStyle} />
                    <Button variant="ghost" onClick={copyLink}>{copied ? t('Copié ✓') : t('Copier')}</Button>
                  </div>
                </label>
              </div>
            )}
            </div>

            {/* Sticky footer — always visible */}
            <div
              className="shrink-0 flex items-center justify-end gap-2 px-5 sm:px-6 py-4 border-t"
              style={{ borderColor: 'var(--color-border-subtle)', background: 'var(--color-bg-elevated)' }}
            >
              {!shareUrl ? (
                <>
                  <Button variant="ghost" onClick={closeModal} disabled={busy}>{t('Annuler')}</Button>
                  <Button onClick={handleSubmit} loading={busy} disabled={busy}>{t("Créer l'album + lien client")}</Button>
                </>
              ) : (
                <>
                  {waUrl() ? (
                    <a href={waUrl()!} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white min-h-[44px]" style={{ background: '#25D366' }}>
                      {t('Partager sur WhatsApp')}
                    </a>
                  ) : <span className="text-xs mr-auto" style={{ color: 'var(--color-text-tertiary)' }}>{t('Aucun numéro — copiez le lien.')}</span>}
                  <Button onClick={closeModal}>{t('Terminé')}</Button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Admin Edit Album Card ──────────────────────────────────────────────────
// Lets admin walk through the customer's editor flow (upload + cover + layout).
// One link per album in the bundle (Solo=1, Duo=2, Trio=3).
function AdminEditAlbumCard({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  // Albums come from the REAL album rows, not from `bundle`.
  //
  // Order #1122 was a Solo priced at 599 (the Duo price) that actually had two
  // albums. Counting from `bundle` gave one row, so album 2 had no editor, no
  // cover picker and no way to reach it from the admin at all — it was invisible
  // work that would have shipped wrong. `album_pdfs` maps every album_sessions
  // row (worker/src/routes/orders.ts:409), so its indexes are the truth.
  //
  // Falls back to the bundle count only when the list is missing, which keeps
  // the card working while the order query is still loading.
  const albumIndexes = resolveAlbumIndexes(order.bundle, order.album_pdfs);
  const albumCount = albumIndexes.length;
  const destinations = [order.destination_1, order.destination_2, order.destination_3];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        {/* min-w-0: flex items default to min-content width, so this title +
            subtitle block could not compress and pushed the row wide. */}
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-800">{t("Édition de l'album")}</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {t("Ouvrez l'éditeur pour téléverser les photos, choisir une couverture et organiser la mise en page")}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {albumIndexes.map((idx) => {
          // Destination is keyed by the album's OWN index, not by loop position,
          // so a gap in album numbering can't shift every label by one.
          const dest = destinations[idx - 1];
          return (
            <div key={idx} className="border border-gray-100 rounded-lg overflow-hidden">
              <AdminAlbumCoverRow
                order={order}
                shopifyOrderId={shopifyOrderId}
                albumIndex={idx}
                albumCount={albumCount}
                destination={dest ?? null}
                year={String(new Date(order.created_at ?? Date.now()).getFullYear())}
              />
              <EditorUnlockControl
                shopifyOrderId={shopifyOrderId}
                albumIndex={idx}
                orderStatus={order.status}
              />
              <AlbumPdfImportControl
                shopifyOrderId={shopifyOrderId}
                albumIndex={idx}
                orderStatus={order.status}
              />
              {/* Remove this album — only when more than one album exists (the
                  backend refuses to remove the last). */}
              {albumCount > 1 && (
                <RemoveAlbumControl
                  order={order}
                  shopifyOrderId={shopifyOrderId}
                  albumIndex={idx}
                  destination={dest ?? null}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Per-album row with inline cover thumb (2026-06-30) ──────────────────────
// Each album row carries a compact, clearly-visible cover thumb (~48×64, 3:4)
// to the LEFT of the album-number badge so the admin SEES each album's chosen
// cover at a glance. The thumb is a <button> (NOT inside the editor <Link>) so
// tapping it opens a cover picker instead of the editor. Per-album scoping:
// each row owns its OWN modal state + selected category, resolves ITS cover_id
// from the cached follow-up sessions + the shared ['covers-flow'] covers list,
// and runs its OWN set-cover mutation. Reuses the customer-flow <StepDesign>
// (search + categories + Couverture vierge + destination auto-match) inside a
// Modal so admin + customer pick covers through the exact same surface.
function AdminAlbumCoverRow({
  order,
  shopifyOrderId,
  albumIndex,
  albumCount,
  destination,
  year,
}: {
  order: Order;
  shopifyOrderId: string;
  albumIndex: number;
  albumCount: number;
  destination: string | null;
  year: string;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cat, setCat] = useState<string | null>(null);

  // Shared with the customer flow (same query keys → react-query dedupes, zero
  // extra fetches if either surface already loaded them).
  const { data: coversData } = useQuery({
    queryKey: ['covers-flow'],
    queryFn: () => coversApi.list(),
  });
  const { data: categoriesData } = useQuery({
    queryKey: ['cover-categories-flow'],
    queryFn: () => coversApi.categories(),
  });
  // Source of truth for THIS album's saved cover_id. Shared with the pipeline
  // rail / SmartFollowUpCard via the same key, so no extra network cost.
  const { data: fuData } = useQuery({
    queryKey: ['admin-order-follow-up', shopifyOrderId],
    queryFn: () => ordersApi.followUp(shopifyOrderId),
    enabled: !!shopifyOrderId,
    staleTime: 30_000,
  });

  const covers: Cover[] = coversData?.covers ?? [];
  const categories: CoverCategory[] = categoriesData?.categories ?? [];

  // Resolve cover_id → Cover. cover_id is stored per album_sessions row keyed by
  // album_index; null/missing = no cover chosen yet ("Couverture à venir").
  const session = (fuData?.sessions ?? []).find((s) => s.album_index === albumIndex);
  const coverId = session?.cover_id ?? null;
  const selectedCover: Cover | null = coverId ? covers.find((c) => c.id === coverId) ?? null : null;
  const thumbUrl = selectedCover
    ? pickerThumbUrl(selectedCover.thumbnail_url ?? selectedCover.design_url, 200)
    : null;

  const setCoverMutation = useMutation({
    // 'Couverture vierge' arrives as the synthetic '__blank__' cover from
    // StepDesign → persist as cover_id:null (matches the customer flow's own
    // blank handling), so the row falls back to the empty placeholder.
    mutationFn: (c: Cover) =>
      sessionsApi.upsert(shopifyOrderId, albumIndex, {
        cover_id: c.id === '__blank__' ? null : c.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-order-follow-up', shopifyOrderId] });
      setPickerOpen(false);
    },
  });

  return (
    <>
      <div className="flex items-center gap-2.5 p-3 group">
        {/* Cover thumb / empty placeholder — a real button OUTSIDE the editor
            <Link>. stopPropagation isn't needed (it's a sibling, not a child),
            but keep it explicit-safe. ~48×64 3:4, rounded, subtle border. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
          title={t('Choisir / changer la couverture')}
          className="shrink-0 w-12 h-16 rounded-lg overflow-hidden border transition-all hover:ring-2 hover:ring-brand-500/40 hover:-translate-y-px"
          style={{ borderColor: 'rgba(201,162,74,0.35)', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
        >
          {thumbUrl ? (
            <img src={thumbUrl} alt={selectedCover?.name ?? ''} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <span className="w-full h-full flex flex-col items-center justify-center gap-0.5 bg-brand-500/5 text-brand-500">
              <span className="text-base leading-none">＋</span>
              <span className="text-[8px] leading-tight text-center px-0.5 text-gray-500">
                {t('Couverture à venir')}
              </span>
            </span>
          )}
        </button>

        <Link
          to={`/editor/${shopifyOrderId}/${albumIndex}/edit`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-between min-w-0 hover:opacity-90 transition-opacity"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-8 h-8 shrink-0 rounded-lg bg-brand-500/10 text-brand-500 flex items-center justify-center text-sm font-semibold">
              {albumIndex}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">
                {t('Album {n}', { n: albumIndex })}{albumCount > 1 ? ` / ${albumCount}` : ''}
              </p>
              <p className="text-xs text-gray-400 truncate">{destination || t('Destination non définie')}</p>
            </div>
          </div>
          <span className="shrink-0 text-xs text-gray-400 group-hover:text-brand-500 transition-colors ml-2">
            {t("Ouvrir l'éditeur →")}
          </span>
        </Link>
      </div>

      <Modal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        size="lg"
        title={t('Couverture — Album {n}', { n: albumIndex })}
      >
        <StepDesign
          covers={covers}
          categories={categories}
          selectedCover={selectedCover}
          selectedCategory={cat}
          onCategoryChange={setCat}
          onSelectCover={(c) => setCoverMutation.mutate(c)}
          destination={destination ?? ''}
          year={year}
          shopifyOrderId={shopifyOrderId}
          albumIndex={albumIndex}
          customerName={order.customer_name ?? null}
          customerPhone={order.customer_phone ?? null}
        />
      </Modal>
    </>
  );
}

// ─── Remove album control ────────────────────────────────────────────────────
// Per-album "Retirer" button (Duo/Trio only). Confirms, then deletes the album:
// the backend renumbers the rest, reprices DOWN by tier and syncs Shopify. If the
// order already has an AMEEX parcel (shipped), the confirm warns that the AMEEX
// bon won't be auto-updated.
function RemoveAlbumControl({
  order,
  shopifyOrderId,
  albumIndex,
  destination,
}: {
  order: Order;
  shopifyOrderId: string;
  albumIndex: number;
  destination: string | null;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'warn'; text: string } | null>(null);

  // "Shipped" = an AMEEX parcel has been registered, or the order/AMEEX status is
  // already past pick-up. ameex_parcel_code is the most reliable "a bon exists".
  const isShipped =
    Boolean(order.ameex_parcel_code) ||
    order.ameex_status === 'in_progress' ||
    order.ameex_status === 'distribution' ||
    order.ameex_status === 'delivered' ||
    order.status === 'shipped' ||
    order.status === 'delivered';

  async function handleRemove() {
    const base = t(
      "Retirer l'album {n} ({d}) ? Le prix passera à la formule inférieure et Shopify sera mis à jour. Action définitive : les photos de cet album seront supprimées.",
      { n: albumIndex, d: destination ?? t('sans destination') },
    );
    const msg = isShipped
      ? `${t('⚠️ Commande déjà expédiée via AMEEX — le bon AMEEX ne sera pas mis à jour automatiquement.')}\n\n${base}`
      : base;
    if (!window.confirm(msg)) return;

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { order: updated, shopify_synced } = await ordersApi.removeAlbum(shopifyOrderId, albumIndex);
      // Refresh the same queries the add-album flow invalidates so the page
      // reprices + drops the album row.
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['order-pricing', shopifyOrderId] });
      if (shopify_synced === false) {
        setNotice({ kind: 'warn', text: t('Prix mis à jour, mais la synchro Shopify a échoué — réessayez.') });
      } else {
        setNotice({
          kind: 'ok',
          text: t('Album retiré · {b} · {p} MAD', {
            b: updated.bundle ?? '',
            p: updated.total_price ?? '',
          }),
        });
      }
    } catch (e) {
      setError((e as Error)?.message ?? t("Échec du retrait de l'album."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-1.5 px-3 py-2 bg-gray-50 border-t border-gray-100">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-gray-400 truncate">
          {t('Retirer définitivement cet album de la commande')}
        </span>
        <button
          type="button"
          onClick={handleRemove}
          disabled={busy}
          className="shrink-0 inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2.5 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
        >
          {busy ? t('Retrait…') : t('Retirer')}
        </button>
      </div>
      {notice && (
        <p
          className={`text-[11px] px-2 py-1 rounded ${notice.kind === 'warn' ? '' : 'text-[#3D7A4E] dark:text-status-done'}`}
          style={
            notice.kind === 'warn'
              ? { color: 'var(--color-error)', background: 'rgba(184,92,61,0.08)' }
              : { background: 'rgba(61,122,78,0.10)' }
          }
        >
          {notice.text}
        </p>
      )}
      {error && (
        <p className="text-[11px] px-2 py-1 rounded" style={{ color: 'var(--color-error)', background: 'rgba(184,92,61,0.08)' }}>
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Editor unlock control ───────────────────────────────────────────────────
// After a customer submits, the editor locks them out. This lets an admin grant
// a one-shot re-entry (e.g. so the customer can re-upload photos that failed to
// upload) and copy a link to send them. Re-submitting auto-relocks; the admin
// can also re-lock here. Only meaningful once the album is submitted.
function EditorUnlockControl({
  shopifyOrderId,
  albumIndex,
  orderStatus,
}: {
  shopifyOrderId: string;
  albumIndex: number;
  orderStatus: string;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);

  // Only awaiting-confirmation albums are locked; before submit the editor is
  // already open, so the control is informational/hidden.
  const isSubmitted = orderStatus === 'album-submitted';

  const { data } = useQuery({
    queryKey: ['album-session', shopifyOrderId, albumIndex],
    queryFn: () => sessionsApi.get(shopifyOrderId, albumIndex),
    enabled: isSubmitted,
  });
  const unlocked = data?.session?.editor_unlocked === true;

  // Share-for-upload window: open lets the customer re-enter and APPEND photos
  // to the submitted album. Auto-closes at upload_allowed_until (48h) / resubmit.
  const uploadWindowOpen = isUploadWindowOpen(data?.session);
  const uploadWindowUntil = data?.session?.upload_allowed_until ?? null;
  const [shareCopied, setShareCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: (next: boolean) => ordersApi.setEditorUnlocked(shopifyOrderId, albumIndex, next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['album-session', shopifyOrderId, albumIndex] }),
  });

  const shareMutation = useMutation({
    mutationFn: () => ordersApi.shareForUpload(shopifyOrderId, albumIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['album-session', shopifyOrderId, albumIndex] });
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    },
  });

  const copyLink = () => {
    const url = `${window.location.origin}/editor/${shopifyOrderId}/${albumIndex}/edit`;
    const flash = () => { setCopied(true); setTimeout(() => setCopied(false), 1600); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(flash, () => window.prompt(t('Lien éditeur :'), url));
    } else {
      window.prompt(t('Lien éditeur :'), url);
    }
  };

  if (!isSubmitted) return null;

  return (
    <div className="flex flex-col gap-1.5 px-3 py-2 bg-gray-50 border-t border-gray-100">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${unlocked ? 'bg-emerald-500' : 'bg-gray-300'}`} />
          <span className="text-[11px] text-gray-500 truncate">
            {unlocked ? t('Éditeur déverrouillé pour le client') : t('Éditeur verrouillé (album soumis)')}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {unlocked && (
            <button
              type="button"
              onClick={copyLink}
              className="text-[11px] font-medium px-2 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-white transition-colors"
            >
              {copied ? t('✓ Copié') : t('Copier le lien')}
            </button>
          )}
          <button
            type="button"
            onClick={() => mutation.mutate(!unlocked)}
            disabled={mutation.isPending}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors disabled:opacity-50 ${
              unlocked
                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                : 'bg-brand-500 text-white hover:bg-brand-600'
            }`}
          >
            {mutation.isPending ? '…' : unlocked ? t('Verrouiller') : t('Déverrouiller')}
          </button>
        </div>
      </div>

      {/* Share-for-upload: opens a 48h window letting the customer append more
          photos to the submitted album, then copies the editor link. */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-gray-500 truncate">
          {uploadWindowOpen && uploadWindowUntil
            ? t("Fenêtre d'upload ouverte jusqu'au {date}", { date: new Date(uploadWindowUntil).toLocaleString('fr-FR') })
            : t('Autoriser le client à ajouter des photos (48h)')}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {uploadWindowOpen && (
            <button
              type="button"
              onClick={copyLink}
              className="text-[11px] font-medium px-2 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-white transition-colors"
            >
              {copied ? t('✓ Copié') : t('Copier le lien')}
            </button>
          )}
          <button
            type="button"
            onClick={() => shareMutation.mutate()}
            disabled={shareMutation.isPending}
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-amber-500 text-white hover:bg-amber-600 transition-colors disabled:opacity-50"
          >
            {shareMutation.isPending
              ? '…'
              : shareCopied
                ? t('✓ Fenêtre ouverte')
                : uploadWindowOpen ? t('Prolonger 48h') : t('Partager pour upload')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Upload link card ─────────────────────────────────────────────────────────
// Generate / regenerate / revoke a magic upload link for the customer to send
// in their photos without needing an account. Lives at /upload/{token} in the
// React app.
function UploadLinkCard({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const now = Date.now();
  const expiresAt = order.upload_token_expires_at ? new Date(order.upload_token_expires_at).getTime() : 0;
  const isExpired = !!order.upload_token_expires_at && expiresAt < now;
  const isRevoked = !!order.upload_token_revoked_at;
  const isActive = !!order.upload_token && !isExpired && !isRevoked;

  // We don't store the full URL on the order — derive it from the token.
  // Use the API URL's origin as a base and rewrite to the app domain. The
  // worker's generate endpoint returns the canonical URL — we mirror it here.
  // For display purposes, just construct from window.location.origin.
  const uploadUrl = order.upload_token
    ? `${window.location.origin}/upload/${order.upload_token}`
    : null;

  const generateMutation = useMutation({
    mutationFn: () => uploadLinkApi.generate(shopifyOrderId),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const revokeMutation = useMutation({
    mutationFn: () => uploadLinkApi.revoke(shopifyOrderId),
    onSuccess: () => {
      setError(null);
      setConfirmingRevoke(false);
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    },
    onError: (e: Error) => setError(e.message),
  });

  async function copyLink() {
    if (!uploadUrl) return;
    try {
      await navigator.clipboard.writeText(uploadUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  }

  function whatsappUrl(): string | null {
    if (!uploadUrl || !order.customer_phone) return null;
    const digits = order.customer_phone.replace(/\D/g, '');
    const intl = digits.startsWith('212') ? digits :
                 digits.startsWith('0') ? '212' + digits.slice(1) :
                 digits;
    const firstName = (order.customer_name ?? '').split(' ')[0] || '';
    const text = `Bonjour ${firstName} 👋

Voici votre lien sécurisé pour envoyer vos photos à Storiz :
${uploadUrl}

Cliquez, glissez vos photos et c'est tout. On s'occupe du reste 📸✨`;
    return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-700">{t("Lien d'upload client")}</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {t('Partagez ce lien pour que le client envoie ses photos sans se connecter.')}
          </p>
        </div>
        {isActive && (
          <span className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
            {t('Actif')}
          </span>
        )}
        {isRevoked && (
          <span className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5">
            {t('Révoqué')}
          </span>
        )}
        {!isActive && !isRevoked && order.upload_token && isExpired && (
          <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
            {t('Expiré')}
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mb-3">
          {error}
        </div>
      )}

      {isActive && uploadUrl ? (
        <>
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2 mb-3">
            <input
              readOnly
              value={uploadUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 bg-transparent text-sm text-gray-700 font-mono outline-none truncate"
            />
            <Button size="sm" variant="secondary" onClick={copyLink}>
              {copied ? t('✓ Copié') : t('Copier')}
            </Button>
          </div>

          <div className="flex gap-2 flex-wrap">
            {whatsappUrl() && (
              <a
                href={whatsappUrl()!}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#25D366] text-white text-sm font-medium hover:opacity-90"
              >
                WhatsApp
              </a>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => generateMutation.mutate()}
              loading={generateMutation.isPending}
            >
              {t('Régénérer (nouveau lien)')}
            </Button>
            {confirmingRevoke ? (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => revokeMutation.mutate()}
                  loading={revokeMutation.isPending}
                >
                  {t('Confirmer la révocation')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmingRevoke(false)}>
                  {t('Annuler')}
                </Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setConfirmingRevoke(true)}>
                {t('Révoquer')}
              </Button>
            )}
          </div>

          <div className="text-xs text-gray-400 mt-3 space-y-0.5">
            {order.upload_token_expires_at && (
              <div>
                {t("Valide jusqu'au {date}", { date: new Date(order.upload_token_expires_at).toLocaleDateString('fr-FR', {
                  day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                }) })}
              </div>
            )}
            {order.client_photos_first_upload_at && (
              <div className="text-green-700">
                {t('Premier upload : {date}', { date: new Date(order.client_photos_first_upload_at).toLocaleString('fr-FR') })}
              </div>
            )}
            {order.client_photos_last_upload_at && (
              <div className="text-green-700">
                {t('Dernier upload : {date}', { date: new Date(order.client_photos_last_upload_at).toLocaleString('fr-FR') })}
              </div>
            )}
          </div>
        </>
      ) : (
        <Button onClick={() => generateMutation.mutate()} loading={generateMutation.isPending}>
          {order.upload_token ? t('Générer un nouveau lien') : t("Générer un lien d'upload")}
        </Button>
      )}
    </div>
  );
}

// ─── Client photos card ───────────────────────────────────────────────────────
// Shows photos uploaded via the magic link, grouped by submission batch (each
// "Envoyer" click on the customer side = one batch). Lets admin wipe all
// photos for a session — useful for resetting test orders or starting over.
function ClientPhotosCard({ order }: { order: Order }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [confirmingWipe, setConfirmingWipe] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-album photo-manager popup: clicking an album row opens the fast square
  // grid (select / delete) scoped to that album's session.
  const [photoModal, setPhotoModal] = useState<{ sessionId: string; albumLabel: string } | null>(null);

  // Routed through the worker (service role) instead of direct Supabase
  // to avoid any RLS edge cases — admin should always see everything.
  const { data, isLoading, error: queryError } = useQuery({
    queryKey: ['order-photos-summary', order.shopify_order_id],
    queryFn: () => ordersApi.photosSummary(order.shopify_order_id),
    enabled: !!order.shopify_order_id,
    refetchInterval: 5000,        // live-ish: customer uploading? See it appear.
    refetchOnWindowFocus: true,
  });

  const wipeMutation = useMutation({
    mutationFn: (sessionId: string) => photosApi.wipeSession(sessionId),
    onSuccess: () => {
      setError(null);
      setConfirmingWipe(null);
      queryClient.invalidateQueries({ queryKey: ['order-photos-summary', order.shopify_order_id] });
      queryClient.invalidateQueries({ queryKey: ['order', order.shopify_order_id] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const convertMutation = useMutation({
    mutationFn: (sessionId: string) => photosApi.convertHeicSession(sessionId),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['order-photos-summary', order.shopify_order_id] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (isLoading) {
    return <div className="bg-white rounded-xl border border-gray-200 p-5 h-24 animate-pulse" />;
  }

  const sessions = data?.sessions ?? [];
  const allPhotos = data?.photos ?? [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-700">{t('Photos client')}</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {t("Photos téléversées par le client via le lien d'upload.")}
          </p>
        </div>
        <span className="text-xs font-medium text-gray-500">
          {t('{n} au total', { n: allPhotos.length })}
        </span>
      </div>
      {/* The photo dedup (migration 267): counts above are UNIQUE photos. */}
      <PhotoDedupLine shopifyOrderId={order.shopify_order_id} />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mb-3">
          {error}
        </div>
      )}
      {queryError && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mb-3">
          {t('Erreur de chargement :')} {queryError.message}
        </div>
      )}

      {sessions.length === 0 ? (
        <p className="text-sm text-gray-400">{t('Aucun album.')}</p>
      ) : (
        <div className="space-y-3">
          {sessions.map((session) => {
            const sessionPhotos = allPhotos.filter((p) => p.album_session_id === session.id);
            // Group by submission_batch_id. Photos without a batch (uploaded
            // via the editor flow, not the magic link) get bucketed as "Editor".
            const batches = new Map<string, typeof sessionPhotos>();
            for (const p of sessionPhotos) {
              const key = p.submission_batch_id ?? '__editor__';
              const arr = batches.get(key) ?? [];
              arr.push(p);
              batches.set(key, arr);
            }
            // Sort batches by their newest photo (most recent first).
            const sortedBatches = [...batches.entries()].sort((a, b) => {
              const aMax = Math.max(...a[1].map((p) => new Date(p.submitted_at ?? p.created_at).getTime()));
              const bMax = Math.max(...b[1].map((p) => new Date(p.submitted_at ?? p.created_at).getTime()));
              return bMax - aMax;
            });

            const isConfirming = confirmingWipe === session.id;
            const isWiping = wipeMutation.isPending && wipeMutation.variables === session.id;

            return (
              <div key={session.id} className="border border-gray-100 rounded-lg p-3">
                <button
                  type="button"
                  onClick={() => setPhotoModal({
                    sessionId: session.id,
                    // The album's NAME — its printed title, else its order words (lib/titleSource.ts).
                    albumLabel: `${t('Album {n}', { n: session.album_index })}${albumName(session) ? ` · ${albumName(session)}` : ''}`,
                  })}
                  className="w-full flex items-center justify-between mb-2 text-left rounded-md -mx-1 px-1 py-1 hover:bg-gray-50 transition cursor-pointer group"
                  title={t('Gérer les photos de cet album')}
                >
                  <div className="text-sm font-semibold text-gray-700 group-hover:text-brand-600 transition">
                    {t('Album {n}', { n: session.album_index })}
                    {albumName(session) && <span className="text-gray-400 font-normal"> · {albumName(session)}</span>}
                  </div>
                  <span className="flex items-center gap-1.5 text-xs text-gray-500">
                    {t('{n} photos', { n: sessionPhotos.length })}
                    {/* Grid/expand affordance — signals the row opens the
                        square photo-grid manager. */}
                    <span
                      aria-hidden
                      className="shrink-0 w-5 h-5 rounded-md border border-gray-200 flex items-center justify-center text-gray-400 group-hover:text-brand-500 group-hover:border-brand-500/40 transition"
                    >
                      <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="5" height="5" rx="1" />
                        <rect x="9" y="2" width="5" height="5" rx="1" />
                        <rect x="2" y="9" width="5" height="5" rx="1" />
                        <rect x="9" y="9" width="5" height="5" rx="1" />
                      </svg>
                    </span>
                  </span>
                </button>
                {/* « Thème de l'album » — the same bar as in the editor (admin only; empty = global). */}
                <div className="mb-2">
                  <AlbumThemeBar sessionId={session.id} />
                </div>

                {sessionPhotos.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">{t('Aucune photo encore.')}</p>
                ) : (
                  <ul className="space-y-1.5">
                    {sortedBatches.map(([key, batch]) => {
                      const isEditor = key === '__editor__';
                      const newest = batch.reduce((m, p) => {
                        const t = new Date(p.submitted_at ?? p.created_at).getTime();
                        return t > m ? t : m;
                      }, 0);
                      return (
                        <li key={key} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full ${isEditor ? 'bg-gray-400' : 'bg-brand-500'}`} />
                            <span className="text-gray-700 font-medium">
                              {isEditor ? t('Éditeur Storiz') : t('Lien client')}
                            </span>
                            <span className="text-gray-400">
                              · {batch.length > 1 ? t('{n} photos', { n: batch.length }) : t('{n} photo', { n: batch.length })}
                            </span>
                          </div>
                          <span className="text-gray-400">
                            {new Date(newest).toLocaleString('fr-FR', {
                              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                            })}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {sessionPhotos.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    {isConfirming ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-700">
                          {t('Supprimer définitivement les {n} photos ?', { n: sessionPhotos.length })}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => wipeMutation.mutate(session.id)}
                          loading={isWiping}
                        >
                          {t('Oui, supprimer')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmingWipe(null)}>
                          {t('Annuler')}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          type="button"
                          onClick={() => convertMutation.mutate(session.id)}
                          disabled={convertMutation.isPending}
                          className="text-xs font-medium text-brand-500 hover:underline disabled:opacity-50"
                        >
                          {convertMutation.isPending
                            ? t('🔄 Conversion en cours...')
                            : t('🔄 Convertir les HEIC en JPEG')}
                        </button>
                        {convertMutation.isSuccess && convertMutation.data && (
                          <span className="text-xs text-green-700">
                            {t('✓ {done}/{total} converties', { done: convertMutation.data.succeeded, total: convertMutation.data.total })}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => setConfirmingWipe(session.id)}
                          className="text-xs font-medium text-red-600 hover:underline"
                        >
                          {t('🗑 Supprimer toutes les photos')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* One per-album photo manager, driven by the clicked album row. */}
      <AdminAlbumPhotosModal
        isOpen={photoModal !== null}
        onClose={() => setPhotoModal(null)}
        sessionId={photoModal?.sessionId ?? null}
        albumLabel={photoModal?.albumLabel ?? ''}
        onChanged={() => {
          queryClient.invalidateQueries({ queryKey: ['order-photos-summary', order.shopify_order_id] });
        }}
      />
    </div>
  );
}

// ─── Order Info card ────────────────────────────────────────────────────────
// View shows the full order summary; "Modifier" flips the four customer-contact
// fields (name, phone, email, note) into inputs. Save writes Supabase first
// (source of truth) then best-effort pushes to the live Shopify order; the
// banner reflects whether that Shopify push landed.
function OrderInfoCard({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  // THIS order's frozen extra-page price (migration 230) — the same number the
  // worker bills with. Never the live setting, which only reaches new orders.
  const pricePerPage = resolvePricePerPage(order);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(order.customer_name ?? '');
  const [phone, setPhone] = useState(order.customer_phone ?? '');
  const [email, setEmail] = useState(order.customer_email ?? '');
  const [note, setNote] = useState(order.customer_note ?? '');

  // ── Corriger le prix (admin manual total override) ──────────────────────────
  const hasOverride = order.price_override != null;
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState(
    order.total_price != null ? String(Math.round(Number(order.total_price))) : '',
  );
  const [priceResult, setPriceResult] = useState<
    { shopify_synced: boolean; ameex_synced: boolean; ameex_skipped_reason?: string } | null
  >(null);
  // Re-seed the input when a fresh order lands and we're not mid-edit.
  useEffect(() => {
    if (!editingPrice) {
      setPriceInput(order.total_price != null ? String(Math.round(Number(order.total_price))) : '');
    }
  }, [editingPrice, order.total_price]);

  const correctPrice = useMutation({
    mutationFn: (total: number | null) => ordersApi.correctPrice(shopifyOrderId, total),
    onSuccess: (res) => {
      setPriceResult({
        shopify_synced: res.shopify_synced,
        ameex_synced: res.ameex_synced,
        ameex_skipped_reason: res.ameex_skipped_reason,
      });
      setEditingPrice(false);
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['order-pricing', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
    },
  });

  // Re-seed local inputs whenever a fresh order lands (refetch, poll) and we're
  // not mid-edit, so the form never shows stale values when reopened.
  useEffect(() => {
    if (!editing) {
      setName(order.customer_name ?? '');
      setPhone(order.customer_phone ?? '');
      setEmail(order.customer_email ?? '');
      setNote(order.customer_note ?? '');
    }
  }, [editing, order.customer_name, order.customer_phone, order.customer_email, order.customer_note]);

  const save = useMutation({
    mutationFn: () =>
      ordersApi.updateCustomerInfo(shopifyOrderId, {
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        customer_email: email.trim(),
        customer_note: note,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
      setEditing(false);
    },
  });

  const cancel = () => {
    setName(order.customer_name ?? '');
    setPhone(order.customer_phone ?? '');
    setEmail(order.customer_email ?? '');
    setNote(order.customer_note ?? '');
    save.reset();
    setEditing(false);
  };

  // Origin — its own inline mutation so the team can (re)tag attribution in one
  // click without entering the name/phone edit mode.
  const origin = useMutation({
    mutationFn: (value: string | null) =>
      ordersApi.updateCustomerInfo(shopifyOrderId, { origin: value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
    },
  });

  // Test-order flag (2026-07-17): one-click toggle. Excludes the order from the
  // conversion-rate metric AND the 7-day stale-lead auto-cancel.
  const testFlag = useMutation({
    mutationFn: (value: boolean) =>
      ordersApi.updateCustomerInfo(shopifyOrderId, { is_test: value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
    },
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-700">{t('Informations commande')}</h2>
        <div className="flex items-center gap-2">
          <IgnoreControl shopifyOrderId={shopifyOrderId} />
          {!editing && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              {t('✎ Modifier')}
            </Button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditField label={t('Client')} value={name} onChange={setName} placeholder={t('Nom du client')} />
            <EditField label={t('WhatsApp')} value={phone} onChange={setPhone} placeholder="+212…" type="tel" />
            <EditField label={t('Email')} value={email} onChange={setEmail} placeholder="client@exemple.com" type="email" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">{t('Note client')}</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={t('Adresse, remarques…')}
              className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={!name.trim()}
            >
              {t('Enregistrer')}
            </Button>
            <Button variant="ghost" onClick={cancel} disabled={save.isPending}>
              {t('Annuler')}
            </Button>
          </div>
          {save.isError && (
            <p className="text-sm text-red-600">
              {t("Échec de l'enregistrement :")} {(save.error as Error)?.message ?? t('erreur inconnue')}
            </p>
          )}
        </div>
      ) : (
        <>
          {save.isSuccess && (
            <p className={`text-sm mb-3 flex items-center gap-1 ${save.data?.shopify_synced ? 'text-green-600' : 'text-amber-600'}`}>
              {save.data?.shopify_synced
                ? <><span>✓</span> {t('Enregistré et synchronisé avec Shopify')}</>
                : <><span>⚠</span> {t('Enregistré localement — échec sync Shopify')}{save.data?.shopify_error ? ` : ${save.data.shopify_error}` : ''}</>}
            </p>
          )}
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <InfoItem label={t('N° commande')} value={order.shopify_order_name} />
            <InfoItem label={t('Client')} value={order.customer_name} />
            <InfoItem label={t('WhatsApp')} value={formatPhone(order.customer_phone)} isPhone phone={order.customer_phone} />
            <InfoItem label={t('Email')} value={order.customer_email} />
            <InfoItem label={t('Formule')} value={order.bundle ?? '—'} />
            <InfoItem label={t('Pages')} value={String(order.page_count)} />
            {/* Add-on shown as its OWN line rather than folded into the Prix
                breakdown: the breakdown reads "base + N pages", and burying a
                second charge in there is how a total silently stops being
                explainable to a customer on the phone. */}
            {/* Editable per order (owner, 2026-09-29): pencil → number → save,
                through POST /orders/:id/relief-price, which re-prices the order
                with the one pricing function. 0 = offered, line kept. */}
            {(order.relief_3d || Number(order.relief_3d_cost) > 0) && (
              <ReliefPriceField order={order} shopifyOrderId={shopifyOrderId} />
            )}
            {/* Express delivery and any other accepted option (addons_cost),
                its own line for the same reason as the Relief's. */}
            <OrderExtrasLine order={order} />
            {(() => {
              // Up-to-date price from the order's CURRENT extra-page count.
              // `order.extra_pages` is kept in sync by the worker's
              // checkout-summary self-heal; computeOrderPrice unwinds any
              // persisted extras from total_price before re-applying them so
              // the displayed total never double-counts and always tracks the
              // current page count. Display-only — no write.
              const price = computeOrderPrice({
                total_price: order.total_price,
                extra_pages_cost: order.extra_pages_cost,
                relief_3d_cost: order.relief_3d_cost,
                // Shown on its own line above — out of the « base + N pages ».
                addons_cost: order.addons_cost,
                extraPages: order.extra_pages ?? 0,
                pricePerPage,
              });
              // Under a manual override the total IS the override; show it plainly
              // (no auto breakdown, which wouldn't reflect the corrected figure).
              const displayValue = hasOverride
                ? formatPrice(Number(order.price_override))
                : price.extraPages > 0
                  ? `${formatPrice(price.total)} ${t('({base} + {extra} page(s) suppl. × {rate})', { base: price.basePrice, extra: price.extraPages, rate: pricePerPage })}`
                  : formatPrice(price.total);
              return (
                <div>
                  <dt className="text-xs text-gray-400 mb-0.5 flex items-center gap-1.5">
                    {t('Prix')}
                    {hasOverride && (
                      <span
                        className="inline-flex items-center px-1.5 py-px rounded-full text-[10px] font-medium"
                        style={{ background: 'rgba(201,162,74,0.14)', color: 'var(--color-adm-gold-ink)' }}
                        title={t('Prix corrigé manuellement — le calcul automatique est désactivé.')}
                      >
                        {t('Prix corrigé manuellement')}
                      </span>
                    )}
                  </dt>
                  <dd className="font-medium text-gray-800 text-sm">
                    {!editingPrice ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{displayValue}</span>
                        <button
                          type="button"
                          onClick={() => { setPriceResult(null); setEditingPrice(true); }}
                          className="text-xs underline decoration-dotted hover:no-underline"
                          style={{ color: 'var(--color-adm-gold-ink)' }}
                        >
                          {t('Corriger le prix')}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={priceInput}
                            onChange={(e) => setPriceInput(e.target.value)}
                            autoFocus
                            className="w-28 pl-2.5 pr-10 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">MAD</span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            const n = Number(priceInput);
                            if (!Number.isFinite(n) || n < 0) return;
                            correctPrice.mutate(Math.round(n));
                          }}
                          loading={correctPrice.isPending}
                          disabled={priceInput.trim() === '' || Number(priceInput) < 0}
                        >
                          {t('Enregistrer')}
                        </Button>
                        <button
                          type="button"
                          onClick={() => { setEditingPrice(false); correctPrice.reset(); }}
                          disabled={correctPrice.isPending}
                          className="text-xs text-gray-400 hover:text-gray-600"
                        >
                          {t('Annuler')}
                        </button>
                      </div>
                    )}
                    {/* Sync feedback */}
                    {priceResult && (
                      <p
                        className={`text-[11px] mt-1 flex items-center gap-1 ${priceResult.shopify_synced && (priceResult.ameex_synced || priceResult.ameex_skipped_reason) ? 'text-green-600' : 'text-amber-600'}`}
                      >
                        {priceResult.shopify_synced
                          ? <><span>✓</span> {priceResult.ameex_synced
                              ? t('Synchronisé (Shopify · AMEEX)')
                              : t('Synchronisé (Shopify)')}{priceResult.ameex_skipped_reason ? ` · ${t('AMEEX ignoré')} : ${priceResult.ameex_skipped_reason}` : ''}</>
                          : <><span>⚠</span> {t('Enregistré, mais la synchro Shopify a échoué — réessayez.')}{priceResult.ameex_skipped_reason ? ` (AMEEX : ${priceResult.ameex_skipped_reason})` : ''}</>}
                      </p>
                    )}
                    {correctPrice.isError && (
                      <p className="text-[11px] mt-1 text-red-600">
                        {(correctPrice.error as Error)?.message ?? t('Échec de la correction')}
                      </p>
                    )}
                    {hasOverride && !editingPrice && (
                      <button
                        type="button"
                        onClick={() => { setPriceResult(null); correctPrice.mutate(null); }}
                        disabled={correctPrice.isPending}
                        className="text-[11px] mt-1 underline decoration-dotted hover:no-underline text-gray-500"
                      >
                        {t('Rétablir le prix automatique')}
                      </button>
                    )}
                    <p className="text-[10px] text-gray-400 mt-1 leading-snug">
                      {t('AMEEX se met à jour seulement si un colis existe et n’est pas livré.')}
                    </p>
                  </dd>
                </div>
              );
            })()}
            <InfoItem label={t('Date')} value={formatDate(order.created_at)} />
            <CreatedBecause order={order} />
            <div>
              <dt className="text-xs text-gray-400 mb-0.5">{t('Source')}</dt>
              <dd className="font-medium text-gray-800 text-sm">
                <span className="inline-flex items-center gap-1.5">
                  <SourceBadge source={order.traffic_source} size={18} />
                  {sourceLabel(order.traffic_source)}
                </span>
              </dd>
            </div>
            {/* Origin — manual attribution label, always-editable inline (saves
                on change). Independent of the name/phone edit mode. */}
            <div>
              <dt className="text-xs text-gray-400 mb-0.5">
                {t('Origine')}{origin.isPending && <span className="ml-1 text-gray-300">…</span>}
              </dt>
              <dd>
                <OriginSelect
                  value={order.origin ?? null}
                  disabled={origin.isPending}
                  onChange={(v) => origin.mutate(v)}
                />
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400 mb-0.5">{t('Commande test')}</dt>
              <dd>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-[#C9A24A] w-4 h-4"
                    checked={order.is_test === true}
                    disabled={testFlag.isPending}
                    onChange={(e) => testFlag.mutate(e.target.checked)}
                  />
                  <span className="text-[12px] text-gray-600">
                    {order.is_test ? t('Exclue des stats + auto-annulation') : t('Marquer comme test')}
                  </span>
                </label>
              </dd>
            </div>
            {order.device && (
              <div>
                <dt className="text-xs text-gray-400 mb-0.5">{t('Appareil')}</dt>
                <dd className="font-medium text-gray-800 text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    <DeviceBadge device={order.device} size={18} />
                    {deviceLabel(order.device)}
                  </span>
                </dd>
              </div>
            )}
            {order.destination_1 && (
              <InfoItem label={t('Destination 1')} value={`${order.destination_1}${order.year_1 ? ` · ${order.year_1}` : ''}`} />
            )}
            {order.destination_2 && (
              <InfoItem label={t('Destination 2')} value={`${order.destination_2}${order.year_2 ? ` · ${order.year_2}` : ''}`} />
            )}
            {order.destination_3 && (
              <InfoItem label={t('Destination 3')} value={`${order.destination_3}${order.year_3 ? ` · ${order.year_3}` : ''}`} />
            )}
            {/* Fraud guard (2026-07-08): ordering browser's IP — captured by
                storiz-cod at COD submit. Null on orders predating the feature. */}
            {order.client_ip && (
              <div>
                <dt className="text-xs text-gray-400 mb-0.5">{t('Adresse IP')}</dt>
                <dd className="font-medium text-gray-800 text-sm">
                  <span className="font-mono text-xs">{order.client_ip}</span>
                  {order.client_user_agent && (
                    <span className="block text-[10px] text-gray-400 truncate" title={order.client_user_agent}>
                      {order.client_user_agent}
                    </span>
                  )}
                </dd>
              </div>
            )}
            {order.customer_note && (
              <div className="col-span-2 md:col-span-3">
                <InfoItem label={t('Note client')} value={order.customer_note} />
              </div>
            )}
          </dl>
        </>
      )}
    </div>
  );
}

// Fraud guard — one-tap ban from the order page: blocklists the order's phone
// AND IP (whichever exist). Blocked people get a generic error at the COD
// form, never a "you are banned" signal. Manage entries in Réglages.
/**
 * THE IGNORE BUTTON — we stop chasing, the client is not blocked.
 *
 * ⚠️ WHAT IT DOES NOT DO, SPELLED OUT IN THE CONFIRM. It does not cancel the
 * order, delete a photo, or touch the status or the price. A button that
 * silences a customer has to say plainly what it leaves alone, or nobody
 * dares press it and the waiting list stays full of rows nobody can act on.
 *
 * The assistant still answers if the customer writes: ignoring is OUR side
 * going quiet, never theirs.
 */
function IgnoreControl({ shopifyOrderId }: { shopifyOrderId: string }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['ignored-list'],
    queryFn: () => ordersApi.ignored(),
    staleTime: 30_000,
  });
  const mine = (data?.items ?? []).find(
    (i) => i.kind === 'order' && (i.order?.shopify_order_name === shopifyOrderId
      || i.order?.id === shopifyOrderId),
  ) ?? null;
  // The list keys on the order's uuid; the page knows its shopify id. When the
  // two cannot be matched the button still works — it just cannot pre-colour
  // itself, and a wrong colour would be worse than a neutral one.
  const isIgnored = Boolean(mine);

  const done = () => {
    setErr(null);
    queryClient.invalidateQueries({ queryKey: ['ignored-list'] });
    queryClient.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    queryClient.invalidateQueries({ queryKey: ['pending-follow-up'] });
  };
  const ignore = useMutation({
    mutationFn: (reason: string) => ordersApi.ignore(shopifyOrderId, reason),
    onSuccess: done,
    onError: (e: Error) => setErr(e.message),
  });
  const unignore = useMutation({
    mutationFn: () => ordersApi.unignore(shopifyOrderId),
    onSuccess: done,
    onError: (e: Error) => setErr(e.message),
  });

  if (isIgnored) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          {t('🔕 Ignorée — aucune relance')}
        </span>
        <Button variant="ghost" size="sm" disabled={unignore.isPending} onClick={() => unignore.mutate()}>
          {t('Reprendre le suivi')}
        </Button>
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        disabled={ignore.isPending}
        onClick={() => {
          const reason = window.prompt(t(
            'Ignorer cette commande ?\n\n'
            + '• On arrête TOUTES les relances et les rappels.\n'
            + '• Elle sort des listes « en attente ».\n'
            + "• L'assistant répond quand même si le client écrit.\n\n"
            + 'La commande n\'est PAS annulée : ni le statut, ni le prix, ni les photos ne changent. '
            + 'Réversible à tout moment.\n\nRaison (facultatif) :',
          ), '');
          if (reason === null) return;   // cancelled
          ignore.mutate(reason);
        }}
      >
        {t('🔕 Ignorer')}
      </Button>
      {err && <span className="text-xs text-red-600">{err}</span>}
    </>
  );
}

function BanClientControl({ order }: { order: Order }) {
  const { t } = useT();
  const [done, setDone] = useState(false);
  const ban = useMutation({
    mutationFn: async () => {
      const note = `${order.shopify_order_name} — ${order.customer_name ?? ''}`.trim();
      const jobs: Promise<unknown>[] = [];
      if (order.customer_phone) jobs.push(ordersApi.blocklist.add('phone', order.customer_phone, note));
      if (order.client_ip)      jobs.push(ordersApi.blocklist.add('ip', order.client_ip, note));
      if (jobs.length === 0) throw new Error(t('Ni téléphone ni IP sur cette commande.'));
      await Promise.all(jobs);
    },
    onSuccess: () => setDone(true),
  });
  if (!order.customer_phone && !order.client_ip) return null;
  const targets = [
    order.customer_phone ? `📞 ${order.customer_phone}` : null,
    order.client_ip ? `IP ${order.client_ip}` : null,
  ].filter(Boolean).join(' + ');
  return (
    <div className="mt-4 pt-3 border-t border-gray-100">
      {done ? (
        <p className="text-xs text-green-600">
          {t('✓ Client banni ({targets}) — gérer la liste dans Réglages.', { targets })}
        </p>
      ) : (
        <button
          type="button"
          onClick={() => {
            if (window.confirm(t('Bannir ce client ? {targets} ne pourra plus passer de commande (il verra une erreur générique).', { targets }))) {
              ban.mutate();
            }
          }}
          disabled={ban.isPending}
          className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
        >
          {ban.isPending ? t('Bannissement…') : t('🚫 Bannir ce client (téléphone + IP)')}
        </button>
      )}
      {ban.isError && (
        <p className="text-[11px] mt-1 text-red-600">{(ban.error as Error)?.message ?? t('Échec')}</p>
      )}
    </div>
  );
}

function EditField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
    </div>
  );
}

function InfoItem({
  label,
  value,
  isPhone,
  phone,
}: {
  label: string;
  value: string | null;
  isPhone?: boolean;
  phone?: string | null;
}) {
  return (
    // NOTE: do NOT add `min-w-0` to this wrapper. These grid tracks are
    // content-sized, so a zero minimum let the first cell collapse to 0px while
    // the second expanded to ~1117px — labels stacked on top of each other and
    // values wrapped one character per line (live-verified 2026-07-29).
    // `overflow-wrap` alone is enough to stop a long email overflowing the card.
    <div>
      <dt className="text-xs text-gray-400 mb-0.5">{label}</dt>
      <dd className="font-medium text-gray-800 text-sm" style={{ overflowWrap: 'anywhere' }}>
        {isPhone && phone ? (
          <a
            href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#128C7E] dark:text-[#25D366] hover:underline"
          >
            {value ?? '—'}
          </a>
        ) : (
          value ?? '—'
        )}
      </dd>
    </div>
  );
}

// ─── Danger Zone: hard-delete an order ──────────────────────────────────────
// Typed-confirmation pattern: admin must type the Shopify order name (e.g.
// "#1052") verbatim to enable the red button. This prevents one-click
// destruction of a real customer order. Shopify sync defaults to ON: drafts
// are deleted on Shopify, real orders are cancelled (Shopify API doesn't
// allow hard-deleting real orders).
function DangerZoneCard({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [syncShopify, setSyncShopify] = useState(true);

  const expectedConfirmation = order.shopify_order_name ?? '';

  const del = useMutation({
    mutationFn: () =>
      ordersApi.delete(shopifyOrderId, { sync_shopify: syncShopify, sync_ameex: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
      queryClient.invalidateQueries({ queryKey: ['ameex-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
      navigate('/admin/orders');
    },
  });

  return (
    <div className="bg-red-50/40 border border-red-200 rounded-xl p-5 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-red-800">{t('Zone de danger')}</h2>
          <p className="text-xs text-red-700/80 mt-0.5">
            {t('Supprimer cette commande retire ses photos, PDFs, colis AMEEX et la commande Shopify associée. Action irréversible.')}
          </p>
        </div>
        {!open && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => { setConfirmation(''); setOpen(true); }}
          >
            {t('Supprimer la commande')}
          </Button>
        )}
      </div>

      {open && (
        <div className="bg-white rounded-lg border border-red-200 p-4 space-y-3">
          <div className="text-sm text-gray-700">
            <p className="mb-2">
              {t('Vous êtes sur le point de supprimer définitivement :')}
            </p>
            <ul className="text-xs text-gray-600 list-disc pl-5 space-y-0.5">
              <li><strong>{order.shopify_order_name}</strong> · {order.customer_name ?? '—'} · {order.bundle ?? '—'}</li>
              <li>{t('{n} page(s)', { n: order.page_count })} · {formatPrice(order.total_price)}</li>
              {(order.cover_pdf_url || order.interior_pdf_url || order.full_book_pdf_url) && <li>{t('Les fichiers PDF générés')}</li>}
              <li>{t('Toutes les photos uploadées par le client')}</li>
              {order.ameex_parcel_code && <li>{t('Le colis AMEEX')} <span className="font-mono">{order.ameex_parcel_code}</span> {t('(annulé chez AMEEX)')}</li>}
            </ul>
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={syncShopify}
              onChange={(e) => setSyncShopify(e.target.checked)}
              className="rounded"
            />
            {t('Synchroniser avec Shopify (supprime le brouillon ou annule la commande)')}
          </label>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('Tapez')} <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{expectedConfirmation}</span> {t('pour confirmer')}
            </label>
            <input
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder={expectedConfirmation}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-400"
              autoFocus
            />
          </div>

          {del.error && (
            <p className="text-xs text-red-600">{(del.error as Error).message}</p>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={del.isPending}>
              {t('Annuler')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={del.isPending}
              disabled={confirmation.trim() !== expectedConfirmation.trim() || !expectedConfirmation}
              onClick={() => del.mutate()}
            >
              {t('Supprimer définitivement')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Smart WhatsApp Follow-up Card ──────────────────────────────────────────
// Replaces the old generic "Contacter via WhatsApp" link. Detects exactly what
// step the customer stopped at, picks the matching French message + resume
// link, and opens wa.me with the message pre-filled. One click for admin.
//
// Spec: docs/superpowers/specs/2026-06-04-smart-whatsapp-resume-design.md
function SmartFollowUpCard({ shopifyOrderId }: { shopifyOrderId: string }) {
  const { t } = useT();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-order-follow-up', shopifyOrderId],
    queryFn: () => ordersApi.followUp(shopifyOrderId),
    staleTime: 30_000,
  });
  // Settings give us the admin's custom templates (if any). Falls back to the
  // bundled DEFAULT_TEMPLATES via renderMessage when settings is null/empty.
  const { data: settingsData } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => settingsApi.get(),
    staleTime: 5 * 60_000,
  });
  const customTemplates = settingsData?.settings.follow_up_templates ?? null;
  const [expanded, setExpanded] = useState(false);

  // 3D-conversion pitch (2026-06-10): when the customer stalled after upload
  // and a flipbook preview link exists, the relance becomes "your album is
  // already made — come flip through it". Key shared with PreviewLinkCard.
  const { data: review } = useQuery({
    queryKey: ['preview-review', shopifyOrderId],
    queryFn: () => previewLinkApi.adminNotes(shopifyOrderId),
    staleTime: 30_000,
  });
  const previewUrl = review?.preview_url ?? null;

  // Compose modal — the admin SEES and EDITS the exact message before sending.
  const [composeOpen, setComposeOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  // Lock the page scroll while composing — the popup is the only focus.
  useEffect(() => {
    if (!composeOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [composeOpen]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-xs text-gray-500">{t('Calcul du message…')}</p>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-xs text-red-600">{t('Impossible de calculer le message de relance.')}</p>
      </div>
    );
  }

  // Build the input for deriveSubState — convert photo_counts (Record) → Map.
  const photoCounts = new Map<number, number>();
  for (const [k, v] of Object.entries(data.photo_counts)) {
    photoCounts.set(Number(k), v);
  }
  const result = deriveSubState({
    status: data.status,
    bundle: data.bundle,
    sessions: data.sessions as any,
    photoCounts,
    ameexStatus: data.ameex_status,
    previewSent: (data as { preview_sent?: boolean }).preview_sent,
    reviewStatus: (data as { review_status?: string | null }).review_status,
    hasPayment: (data as { has_payment?: boolean }).has_payment,
  });

  const firstName = (data.customer.name ?? '').trim().split(/\s+/)[0] ?? '';
  const destination = (data.destinations as any)[result.lastTouchedAlbum] ?? '';
  // Acompte = base advance × number of albums (Solo 50 / Duo 100 / Trio 150).
  const albumCount = albumCountForBundle(data.bundle as 'Solo' | 'Duo' | 'Trio' | null);
  const acompte = computeAcompte(Math.round(Number(settingsData?.settings.advance_amount ?? 50)), albumCount);
  // All album destinations, in order, for the Album Soumis message which lists
  // every album (Solo→1, Duo→2, Trio→3).
  const destinationsList = Array.from({ length: albumCount }, (_, i) => (data.destinations as Record<number, string | null>)[i + 1])
    .map((d) => (d ?? '').toString().trim())
    .filter(Boolean)
    .join(', ');
  // 3D-conversion pitch wins when the customer stalled post-upload AND the
  // admin already built + linked their flipbook preview. Otherwise the
  // standard sub-state template applies.
  // Which rung of the ladder is next for THIS state (1-based).
  const nextAttempt =
    ((data as { follow_up_counts?: Record<string, number> }).follow_up_counts?.[result.subState] ?? 0) + 1;
  // FINAL RUNG (2026-08-12). This message threatens cancellation, so it is
  // called out before a human sends it — and app_settings
  // .followup_last_message_needs_ok keeps the future 24/7 agent from ever
  // firing it unattended.
  const isLastRung = nextAttempt >= ladderLength(result.subState) && ladderLength(result.subState) > 1;
  const isPitchEligible = PREVIEW_PITCH_SUB_STATES.has(result.subState);
  const isPitch = isPitchEligible && !!previewUrl;
  const message = isPitch
    ? renderPreviewPitch({ firstName, destination, previewLink: previewUrl! }, customTemplates)
    : (customTemplates?.[result.subState]
        // An admin-edited template always wins — otherwise editing it in
        // Réglages would silently do nothing from relance 2 onward.
        ? renderMessage(result.subState, {
            firstName, destination, destinationsList,
            link: data.continue_url, acompte,
            total: data.total_price != null ? Math.round(Number(data.total_price)) : undefined,
          }, customTemplates)
        // Otherwise walk the ladder: attempt = what we have already sent + 1.
        : followUpMessage(result.subState, nextAttempt, {
            firstName, destination, destinationsList,
            link: data.continue_url, acompte,
            total: data.total_price != null ? Math.round(Number(data.total_price)) : undefined,
          }));
  const hasLink = isPitch || SUB_STATES_WITH_LINK.has(result.subState);
  const shownLink = isPitch ? previewUrl! : data.continue_url;

  // Phone normalization — Moroccan numbers stored as "0XXX..." need 212 prefix
  // for wa.me's intl format. Already-prefixed numbers (212..., +212...) pass
  // through unchanged.
  const digits = (data.customer.phone ?? '').replace(/\D/g, '');
  const intl = digits.startsWith('0') ? `212${digits.slice(1)}` : digits;

  // Badge color by funnel position. Funnel states → amber (action needed).
  // Terminal states → muted. 3D pitch → gold (conversion play).
  const badgeColor = isPitch
    ? 'border'
    : hasLink
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-gray-50 text-gray-600 border-gray-200';

  const openCompose = () => {
    setDraft(message);
    setCopied(false);
    setComposeOpen(true);
  };

  // Send = open wa.me with the (possibly edited) draft + record the follow-up
  // in history (fire-and-forget — same append-only log the Relances page uses).
  const sendDraft = () => {
    const url = `https://wa.me/${intl}?text=${encodeURIComponent(draft)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    ordersApi.recordFollowUp(shopifyOrderId, data.status, result.subState)
      .then(() => queryClient.invalidateQueries({ queryKey: ['admin-order-follow-up', shopifyOrderId] }))
      .catch(() => { /* history is best-effort; the message was sent regardless */ });
    setComposeOpen(false);
  };

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard denied — non-fatal */ }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">💬</span>
            <h2 className="text-sm font-semibold text-gray-800">{t('Relance WhatsApp')}</h2>
          </div>
          <p className="text-xs text-gray-500">{formatPhone(data.customer.phone ?? '')}</p>
        </div>
        <span
          className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border ${badgeColor}`}
          style={isPitch ? { background: 'rgba(201,162,74,.1)', borderColor: 'rgba(201,162,74,.4)', color: 'var(--color-adm-gold-ink)' } : undefined}
        >
          {isPitch ? t('💎 Relance 3D — album prêt') : t(SUB_STATE_LABELS[result.subState])}
        </span>
      </div>

      {/* Conversion coach — customer stalled post-upload but no 3D link yet.
          Building their album flips this relance into the strongest pitch we
          have: "votre album est déjà prêt". */}
      {isPitchEligible && !previewUrl && (
        <div className="rounded-lg px-3.5 py-3 text-xs leading-relaxed text-[#6E561E] dark:text-adm-gold-ink" style={{ background: 'rgba(201,162,74,.08)', border: '1px solid rgba(201,162,74,.3)' }}>
          <p className="font-semibold mb-1">{t('💎 Boostez la conversion : créez son album 3D')}</p>
          <p>
            {t("Ce client a envoyé ses photos mais n'a pas continué. Construisez l'album à sa place (éditeur → ✨ Régénérer IA → Commander), puis générez le lien d'aperçu (étape 4) — cette relance deviendra une invitation à feuilleter son album déjà prêt.")}
          </p>
          <div className="flex gap-2 mt-2">
            <a
              href={`/editor/${shopifyOrderId}/1/edit`}
              target="_blank" rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #C9A24A, #A8842F)' }}
            >
              {t("Ouvrir l'éditeur ↗")}
            </a>
            <button
              type="button"
              onClick={() => document.getElementById('sec-preview')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white"
              style={{ border: '1px solid rgba(201,162,74,.4)', color: 'var(--color-adm-gold-ink)' }}
            >
              {t('Carte Aperçu →')}
            </button>
          </div>
        </div>
      )}

      {/* Message preview — collapsed by default, click to expand. */}
      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
        {expanded ? message : truncateMessage(message)}
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-gray-500 hover:text-gray-700 underline"
      >
        {expanded ? t('Masquer le message') : t('Voir le message complet')}
      </button>

      {hasLink && (
        <div className="text-xs text-gray-500 break-all">
          <span className="text-gray-400">{isPitch ? t('Lien 3D') : t('Lien')}&nbsp;:</span>{' '}
          <span className="font-mono">{shownLink}</span>
        </div>
      )}

      <button
        type="button"
        onClick={openCompose}
        className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-[#25D366] text-white rounded-xl font-semibold text-sm hover:bg-[#1ebe5a] transition-colors cursor-pointer"
      >
        <span>{t('Prévisualiser & envoyer')}</span>
        <span>→</span>
      </button>

      {/* ── Compose modal — see and EDIT the exact message before sending.
          PORTALED to <body>: the card sits inside an adm-rise (transform)
          wrapper, which hijacks position:fixed as its containing block in
          Chromium — without the portal the veil/modal anchored to the CARD,
          not the viewport (reported by Yahya: popup stuck at the bottom).
          Veil = blur + 10% dark so the worker's eye lands on the popup. */}
      {composeOpen && createPortal(
        <div
          // a 10 % veil in light; the night scrim in dark (a faint black
          // wash would not separate the popup from a dark page)
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[rgba(17,17,17,0.10)] dark:bg-adm-scrim"
          style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
          onClick={() => setComposeOpen(false)}
        >
          <div
            className="w-full max-w-[480px] bg-white rounded-2xl p-5 shadow-2xl adm-rise"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2.5 mb-1">
              <span className="text-lg">💬</span>
              <h3 className="text-[16px] font-semibold" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                {t('Relance WhatsApp')}
              </h3>
              <button
                type="button"
                onClick={() => setComposeOpen(false)}
                aria-label={t('Fermer')}
                className="ml-auto w-8 h-8 rounded-lg border border-gray-200 text-gray-400 hover:text-gray-700 flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              {t('Pour')} <b className="text-gray-800">{data.customer.name ?? '—'}</b> · {formatPhone(data.customer.phone ?? '')}
              <span
                className="ml-2 px-2 py-0.5 rounded-full text-[10.5px] font-semibold"
                style={{ background: 'rgba(201,162,74,.1)', border: '1px solid rgba(201,162,74,.35)', color: 'var(--color-adm-gold-ink)' }}
              >
                {isPitch ? t('💎 Relance 3D') : t(SUB_STATE_LABELS[result.subState])}
              </span>
            </p>

            <div className="relative">
              <span
                className="absolute -top-2 right-3 z-10 px-2 py-px rounded-full text-[9.5px] font-bold tracking-wide bg-white text-[#0B7E5B] dark:text-[#25D366]"
                style={{ border: '1px solid rgba(37,211,102,.45)' }}
              >
                {t('✏️ MODIFIABLE')}
              </span>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={11}
                className="w-full rounded-xl px-3.5 py-3 text-[13px] leading-relaxed text-gray-800 outline-none resize-y focus:ring-2 focus:ring-[#25D366]/40 bg-[#F2FBF5] dark:bg-adm-surface-2"
                style={{ border: '1px solid rgba(37,211,102,.35)' }}
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-1.5">
              {t("Le message est libre — modifiez le texte avant l'envoi. Le lien reste cliquable côté client.")}
            </p>

            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={copyDraft}
                className={`px-3.5 py-2.5 rounded-xl text-xs font-semibold bg-white ${copied ? 'text-[#047857] dark:text-status-done' : 'text-adm-gold-ink'}`}
                style={{ border: '1px solid rgba(201,162,74,.4)' }}
              >
                {copied ? t('✓ Copié') : t('Copier')}
              </button>
              <button
                type="button"
                onClick={() => setDraft(message)}
                className="px-3.5 py-2.5 rounded-xl text-xs font-semibold bg-white"
                style={{ border: '1px solid rgba(201,162,74,.4)', color: 'var(--color-adm-gold-ink)' }}
              >
                {t('Réinitialiser')}
              </button>
              {/* Final rung — this message threatens cancellation. Called out so
                  nobody fires it on autopilot (2026-08-12). */}
              {isLastRung && (
                <span
                  className="w-full text-[11px] font-semibold px-3 py-2 rounded-lg bg-[#FEF2F2] dark:bg-status-stuck-bg text-status-stuck"
                >
                  {t('⚠️ Dernière relance ({n}/{n}) — ce message annonce une annulation. Relisez-le avant d’envoyer.',
                    { n: ladderLength(result.subState) })}
                </span>
              )}
              <button
                type="button"
                onClick={sendDraft}
                disabled={!draft.trim()}
                className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white bg-[#25D366] hover:bg-[#1ebe5a] transition-colors disabled:opacity-50"
                style={{ boxShadow: '0 3px 12px rgba(37,211,102,.3)' }}
              >
                {t('Envoyer sur WhatsApp →')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function truncateMessage(msg: string): string {
  const lines = msg.split('\n');
  if (lines.length <= 1 && msg.length <= 120) return msg;
  const first = lines[0] ?? '';
  return first.length > 120 ? `${first.slice(0, 120)}…` : `${first}…`;
}
